import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useTenant } from "../contexts/TenantContext";
import { useAuth } from "../contexts/AuthContext";
import {
    Box, Typography, Card, Grid, TextField, Switch, Button,
    Divider, InputAdornment, FormControl, Select, MenuItem,
    Alert, Snackbar, Stack, Chip, Avatar, Tooltip,
    Dialog, DialogTitle, DialogContent, DialogActions,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, InputLabel, Badge, CircularProgress, Pagination,
} from "@mui/material";
import {
    Settings as SettingsIcon, Business as OrgIcon, AccessTime as ShiftIcon,
    NotificationsActive as BellIcon, Security as SecurityIcon, Save as SaveIcon,
    CheckCircleOutline as CheckIcon, WbSunny as MorningIcon, Bedtime as EveningIcon,
    Brightness6 as AfternoonIcon, NightsStay as NightIcon,
    Email as EmailIcon, Phone as PhoneIcon, Badge as BadgeIcon,
    Language as TimezoneIcon, CalendarToday as DateIcon,
    MedicalServices as MedIcon, Warning as WarnIcon, Summarize as ReportIcon,
    Lock as LockIcon, Timer as TimerIcon, VerifiedUser as TwoFAIcon,
    LockOpen as LockOpenIcon,
    People as UsersIcon, PersonAdd as PersonAddIcon, Edit as EditIcon,
    Delete as DeleteIcon, Search as SearchIcon, CheckCircle as ActiveIcon,
    Cancel as InactiveIcon, Refresh as RefreshIcon,
    AdminPanelSettings as AdminIcon, ManageAccounts as ManagerIcon,
    LocalHospital as CaregiverIcon, Visibility as ViewIcon,
    VisibilityOff as HideIcon, LockReset as LockResetIcon,
    AddBusiness as AddBusinessIcon, LocationOn as LocationIcon,
    DomainAdd as DomainAddIcon, Star as StarIcon, LocalPharmacy as LocalPharmacyIcon,
    Close as CloseIcon,
} from "@mui/icons-material";
import StockAvailabilityPage from "./StockAvailabilityPage";
import MasterDataTab from "./MasterDataTab";
import LowStockNotificationTab from "./LowStockNotificationTab";
import userService from "../services/userService";
import facilityService from "../services/facilityService";
import securityService, { SecuritySettings } from "../services/securityService";
import notificationService from "../services/notificationService";
import attendanceService from "../services/attendanceService";
import masterDataService from "../services/masterDataService";
import { AttendanceRule, CheckInPolicy } from "../types/attendance.types";
import { FacilityDTO, CareType as FacilityCareType } from "../types/facility.types";
import { UserDTO, UserRole } from "../types/user.types";

// ─── Design Tokens ──────────────────────────────────────────────────────────────
const PRIMARY = "#0077B6";
const TEXT_MAIN = "#1A2B45";
const TEXT_SUB = "#5C6F85";
const BG_PAGE = "linear-gradient(135deg,#F0F4F8 0%,#E8EEF5 100%)";
const CARD_BG = "#FFFFFF";
const BORDER = "#E2E8F0";

// ─── Role Config ────────────────────────────────────────────────────────────────
export const ROLE_CONFIG: Record<string, { label: string; color: string; bg: string; icon: React.ReactNode }> = {
    ADMIN: { label: "Admin", color: "#DC2626", bg: "#FEE2E2", icon: <AdminIcon sx={{ fontSize: 13 }} /> },
    SUPER_ADMIN: { label: "Super Admin", color: "#B45309", bg: "#FFFBEB", icon: <AdminIcon sx={{ fontSize: 13 }} /> },
    // CLIENT_ADMIN: { label: "Client Admin", color: "#7C3AED", bg: "#EDE9FE", icon: <ManagerIcon sx={{ fontSize: 13 }} /> },
    FACILITY_ADMIN: { label: "Facility Manager", color: "#7C3AED", bg: "#EDE9FE", icon: <ManagerIcon sx={{ fontSize: 13 }} /> },
    // MANAGER: { label: "Manager", color: "#7C3AED", bg: "#EDE9FE", icon: <ManagerIcon sx={{ fontSize: 13 }} /> },
    // DOCTOR: { label: "Doctor", color: "#0077B6", bg: "#E0F2FE", icon: <CaregiverIcon sx={{ fontSize: 13 }} /> },
    // SOCIAL_WORKER: { label: "Social Worker", color: "#D97706", bg: "#FEF3C7", icon: <CaregiverIcon sx={{ fontSize: 13 }} /> },
    NURSE: { label: "Caregiver", color: "#0077B6", bg: "#E0F2FE", icon: <CaregiverIcon sx={{ fontSize: 13 }} /> },
    CAREGIVER: { label: "Caregiver", color: "#0077B6", bg: "#E0F2FE", icon: <AddBusinessIcon sx={{ fontSize: 13 }} /> },
    LEAD_CAREGIVER: { label: "Lead Caregiver", color: "#16A34A", bg: "#DCFCE7", icon: <MedIcon sx={{ fontSize: 13 }} /> },
};

// Removing dead FACILITY_MAP

// ─── Sample Users ───────────────────────────────────────────────────────────────
type UserRecord = {
    id: string | number; name: string; email: string; role: string; username?: string;
    status: "active" | "inactive"; lastLogin: string; phone: string; initials: string; avatarColor: string;
    facilityNames?: string[];
    licenseNumber?: string;
    jobTitle?: string;
    profilePictureUrl?: string;
    deleted?: boolean;
};
const SAMPLE_USERS: UserRecord[] = [
    { id: 1, name: "Sarah Mitchell", email: "sarah.mitchell@sunrisecare.com", role: "admin", status: "active", lastLogin: "Today, 09:14 AM", phone: "+44 7700 900001", initials: "SM", avatarColor: "#DC2626" },
    { id: 2, name: "James Carter", email: "james.carter@sunrisecare.com", role: "manager", status: "active", lastLogin: "Today, 08:52 AM", phone: "+44 7700 900002", initials: "JC", avatarColor: "#7C3AED" },
    { id: 3, name: "Emily Nguyen", email: "emily.nguyen@sunrisecare.com", role: "nurse", status: "active", lastLogin: "Yesterday, 11:30 PM", phone: "+44 7700 900003", initials: "EN", avatarColor: "#16A34A" },
    { id: 4, name: "David Okafor", email: "david.okafor@sunrisecare.com", role: "caregiver", status: "active", lastLogin: "Yesterday, 07:00 AM", phone: "+44 7700 900004", initials: "DO", avatarColor: "#0077B6" },
    { id: 5, name: "Priya Sharma", email: "priya.sharma@sunrisecare.com", role: "caregiver", status: "active", lastLogin: "03 Mar 2026, 02:15 PM", phone: "+44 7700 900005", initials: "PS", avatarColor: "#0096C7" },
    { id: 6, name: "Tom Williams", email: "tom.williams@sunrisecare.com", role: "viewer", status: "inactive", lastLogin: "18 Feb 2026, 10:00 AM", phone: "+44 7700 900006", initials: "TW", avatarColor: "#D97706" },
    { id: 7, name: "Laura Bennett", email: "laura.bennett@sunrisecare.com", role: "nurse", status: "active", lastLogin: "Today, 06:45 AM", phone: "+44 7700 900007", initials: "LB", avatarColor: "#059669" },
    { id: 8, name: "Marcus Johnson", email: "marcus.johnson@sunrisecare.com", role: "caregiver", status: "inactive", lastLogin: "10 Jan 2026, 04:30 PM", phone: "+44 7700 900008", initials: "MJ", avatarColor: "#6B7280" },
];

// ─── Facility Types & Sample Data ───────────────────────────────────────────────
type Facility = {
    id: number; name: string;
    email: string; phone: string; code: string; careType: string; subDomain: string;
    address: string; city: string; state: string; country: string; postalCode: string;
    licenseNumber: string; npiNumber: string; medicareProviderNumber: string;
    medicaidProviderNumber: string; capacity: string; notes: string;
    timezone: string; dateFormat: string; isPrimary: boolean;
    createdAt: string;
};
const SAMPLE_FACILITIES: Facility[] = [
    { id: 1, name: "Sunrise Care Home", email: "admin@sunrisecare.com", phone: "+44 20 7946 0958", code: "", careType: "", subDomain: "", address: "12 Sunrise Ave, London, SW1A 1AA", city: "", state: "", country: "", postalCode: "", licenseNumber: "", npiNumber: "", medicareProviderNumber: "", medicaidProviderNumber: "", capacity: "", notes: "", timezone: "GMT", dateFormat: "DD/MM/YYYY", isPrimary: true, createdAt: "2023-01-01T10:00:00Z" },
    { id: 2, name: "Oakwood Nursing Unit", email: "info@oakwoodnursing.com", phone: "+44 20 7946 1122", code: "", careType: "", subDomain: "", address: "88 Oakwood Lane, Manchester, M1 2AB", city: "", state: "", country: "", postalCode: "", licenseNumber: "", npiNumber: "", medicareProviderNumber: "", medicaidProviderNumber: "", capacity: "", notes: "", timezone: "GMT", dateFormat: "DD/MM/YYYY", isPrimary: false, createdAt: "2023-01-02T10:00:00Z" },
    { id: 3, name: "Riverside Care Lodge", email: "contact@riversidelodge.com", phone: "+44 20 7946 3344", code: "", careType: "", subDomain: "", address: "45 River Walk, Birmingham, B1 3CD", city: "", state: "", country: "", postalCode: "", licenseNumber: "", npiNumber: "", medicareProviderNumber: "", medicaidProviderNumber: "", capacity: "", notes: "", timezone: "GMT", dateFormat: "DD/MM/YYYY", isPrimary: false, createdAt: "2023-01-03T10:00:00Z" },
];
const emptyFacility = (): Omit<Facility, "id"> => ({
    name: "", email: "", phone: "", code: "", careType: "", subDomain: "",
    address: "", city: "", state: "", country: "", postalCode: "",
    licenseNumber: "", npiNumber: "", medicareProviderNumber: "", medicaidProviderNumber: "",
    capacity: "", notes: "",
    timezone: "", dateFormat: "DD/MM/YYYY", createdAt: "",
    isPrimary: false
});

// ─── Sidebar Tabs ───────────────────────────────────────────────────────────────
const TABS = [
    { key: "org", label: "Organization", icon: <OrgIcon />, desc: "Facilities & localization", color: "#48CAE4", bg: "#E8F8FB" },
    { key: "attendance", label: "Attendance Rules", icon: <ShiftIcon />, desc: "Shift times & check-in policies", color: "#48CAE4", bg: "#E8F8FB" },
    // { key: "notifications", label: "Notifications", icon: <BellIcon />, desc: "Reminders & system alerts", color: "#0077B6", bg: "#E0F2FE" },
    { key: "users", label: "Users", icon: <UsersIcon />, desc: "User accounts & roles", color: "#48CAE4", bg: "#E8F8FB" },
    { key: "master_data", label: "Master Data", icon: <BadgeIcon />, desc: "Tag Categories & Data", color: "#48CAE4", bg: "#E8F8FB" },
    // { key: "security", label: "Security", icon: <SecurityIcon />, desc: "2FA & session policies", color: "#0077B6", bg: "#E0F2FE" },
    { key: "stock", label: "Stock Availability", icon: <LocalPharmacyIcon />, desc: "Medication stock management", color: "#48CAE4", bg: "#E8F8FB" },
    { key: "low_stock", label: "Low Stock Notification", icon: <BellIcon />, desc: "Medication stock alerts", color: "#48CAE4", bg: "#E8F8FB" },
];

// ─── Sub-Components ─────────────────────────────────────────────────────────────
const SectionCard = ({ title, subtitle, icon, iconColor, iconBg, children, action }: {
    title: string; subtitle?: string; icon: React.ReactNode;
    iconColor: string; iconBg: string; children: React.ReactNode; action?: React.ReactNode;
}) => (
    <Card elevation={0} sx={{
        borderRadius: "20px", border: `1px solid ${BORDER}`, bgcolor: CARD_BG,
        overflow: "hidden", mb: 3, transition: "box-shadow 0.2s",
        "&:hover": { boxShadow: "0 8px 32px rgba(0,119,182,0.08)" },
    }}>
        <Box sx={{
            px: 3, py: 2.5, display: "flex", alignItems: "center", gap: 2,
            borderBottom: `1px solid ${BORDER}`, bgcolor: "#FAFBFC",
            justifyContent: "space-between", flexWrap: "wrap",
        }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Avatar sx={{ bgcolor: iconBg, color: iconColor, width: 42, height: 42, borderRadius: "12px" }}>{icon}</Avatar>
                <Box>
                    <Typography fontWeight={800} sx={{ color: TEXT_MAIN, fontSize: "0.97rem", lineHeight: 1.2 }}>{title}</Typography>
                    {subtitle && <Typography variant="caption" sx={{ color: TEXT_SUB }}>{subtitle}</Typography>}
                </Box>
            </Box>
            {action}
        </Box>
        <Box sx={{ p: 3 }}>{children}</Box>
    </Card>
);

const ToggleRow = ({ icon, iconColor, iconBg, title, desc, checked, onChange, extra, loading, disabled }: {
    icon: React.ReactNode; iconColor: string; iconBg: string;
    title: string; desc: string; checked: boolean; onChange: (v: boolean) => void; extra?: React.ReactNode;
    loading?: boolean; disabled?: boolean;
}) => (
    <Box sx={{
        display: "flex", alignItems: "center", gap: 2,
        p: "14px 18px", borderRadius: "14px",
        border: `1.5px solid ${checked ? iconColor + "30" : BORDER}`,
        bgcolor: checked ? iconColor + "07" : "#FAFBFC",
        transition: "all 0.2s", flexWrap: "wrap",
        "&:hover": { borderColor: iconColor + "50", bgcolor: iconColor + "0A" },
        opacity: (loading || disabled) ? 0.7 : 1,
        pointerEvents: (loading || disabled) ? "none" : "auto"
    }}>
        <Avatar sx={{ bgcolor: iconBg, color: iconColor, width: 36, height: 36, borderRadius: "10px", flexShrink: 0 }}>{icon}</Avatar>
        <Box sx={{ flex: 1, minWidth: 160 }}>
            <Typography variant="body2" fontWeight={700} sx={{ color: TEXT_MAIN, lineHeight: 1.3 }}>{title}</Typography>
            <Typography variant="caption" sx={{ color: TEXT_SUB }}>{desc}</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {loading && <CircularProgress size={16} sx={{ color: iconColor }} />}
            {extra}
            <Switch checked={checked} onChange={e => onChange(e.target.checked)} disabled={loading || disabled}
                sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: iconColor }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: iconColor } }} />
        </Box>
    </Box>
);

const ShiftRow = ({ label, icon, color, bg, start, end, onStart, onEnd, onDelete }: {
    label: string; icon: React.ReactNode; color: string; bg: string;
    start: string; end: string; onStart: (v: string) => void; onEnd: (v: string) => void;
    onDelete?: () => void;
}) => (
    <Box sx={{
        display: "flex", alignItems: "center", gap: { xs: 1.5, sm: 2.5 },
        p: "14px 18px", borderRadius: "14px", border: `1.5px solid ${color}25`,
        bgcolor: bg, flexWrap: "wrap", transition: "box-shadow 0.2s",
        "&:hover": { boxShadow: `0 4px 16px ${color}20` },
    }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: "0 0 auto" }}>
            <Avatar sx={{ bgcolor: color + "20", color: color, width: 34, height: 34, borderRadius: "10px" }}>{icon}</Avatar>
            <Chip label={label} size="small" sx={{ bgcolor: color + "15", color, fontWeight: 800, fontSize: "0.72rem", borderRadius: "8px", minWidth: 80 }} />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1, minWidth: 260 }}>
            <TextField type="time" size="small" value={start} onChange={e => onStart(e.target.value)}
                label="Start" InputLabelProps={{ shrink: true }}
                sx={{ flex: 1, "& .MuiOutlinedInput-root": { borderRadius: "10px", bgcolor: "#fff" } }} />
            <Typography fontWeight={700} sx={{ color, fontSize: "1rem" }}>→</Typography>
            <TextField type="time" size="small" value={end} onChange={e => onEnd(e.target.value)}
                label="End" InputLabelProps={{ shrink: true }}
                sx={{ flex: 1, "& .MuiOutlinedInput-root": { borderRadius: "10px", bgcolor: "#fff" } }} />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {onDelete && (
                <IconButton size="small" onClick={onDelete} sx={{ color: "#DC2626", bgcolor: "#FEE2E2", "&:hover": { bgcolor: "#FECACA" }, borderRadius: "8px" }}>
                    <DeleteIcon sx={{ fontSize: 16 }} />
                </IconButton>
            )}
        </Box>
    </Box>
);

// ─── Role Chip ──────────────────────────────────────────────────────────────────
export const RoleChip = ({ role }: { role: string }) => {
    let r = (role || "").toUpperCase().replace(/\s+/g, '_');
    if (r === 'FACILITY_MANAGER') r = 'FACILITY_ADMIN';
    const cfg = ROLE_CONFIG[r] || ROLE_CONFIG[role] || { label: role, color: "#64748B", bg: "#F1F5F9", icon: null };
    return (
        <Chip icon={cfg.icon as any} label={cfg.label} size="small" sx={{
            bgcolor: cfg.bg, color: cfg.color, fontWeight: 700, fontSize: "0.71rem",
            borderRadius: "8px", border: `1px solid ${cfg.color}25`,
            "& .MuiChip-icon": { color: cfg.color },
        }} />
    );
};

// ─── Status Chip ────────────────────────────────────────────────────────────────
const StatusChip = ({ status, deleted }: { status: "active" | "inactive", deleted?: boolean }) => {
    if (deleted) {
        return (
            <Chip
                icon={<DeleteIcon sx={{ fontSize: "13px !important" }} />}
                label="Deleted"
                size="small"
                sx={{
                    bgcolor: "#F1F5F9",
                    color: "#6B7280",
                    fontWeight: 700, fontSize: "0.71rem", borderRadius: "8px",
                    border: `1px solid #CBD5E125`,
                    "& .MuiChip-icon": { color: "#6B7280" },
                }}
            />
        );
    }
    return (
        <Chip
            icon={status === "active" ? <ActiveIcon sx={{ fontSize: "13px !important" }} /> : <InactiveIcon sx={{ fontSize: "13px !important" }} />}
            label={status === "active" ? "Active" : "Inactive"}
            size="small"
            sx={{
                bgcolor: status === "active" ? "#DCFCE7" : "#F1F5F9",
                color: status === "active" ? "#16A34A" : "#6B7280",
                fontWeight: 700, fontSize: "0.71rem", borderRadius: "8px",
                border: `1px solid ${status === "active" ? "#16A34A" : "#CBD5E1"}25`,
                "& .MuiChip-icon": { color: status === "active" ? "#16A34A" : "#6B7280" },
            }}
        />
    );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────────
const getClientIdFromToken = (): string => {
    try {
        const token = localStorage.getItem('authToken');
        if (!token) return "";
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const payload = JSON.parse(decodeURIComponent(atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')));
        return payload.client_id || payload.clientId || "";
    } catch { return ""; }
};

const CARE_TYPE_MAP: Record<string, FacilityCareType> = {
    "Hospice Care": FacilityCareType.HOSPICE,
    "Assisted Living Facility": FacilityCareType.ALF,
    "ALF with Hospice": FacilityCareType.BOTH,
};

const IANA_TO_TZ_ABBR: Record<string, string> = {
    'Europe/London': 'GMT',
    'Europe/Dublin': 'GMT',
    'America/New_York': 'EST',
    'America/Detroit': 'EST',
    'America/Chicago': 'CST',
    'America/Denver': 'CST',
    'America/Los_Angeles': 'PST',
    'America/Phoenix': 'PST',
    'Asia/Kolkata': 'IST',
    'Asia/Calcutta': 'IST',
};

const normalizeTimezone = (tz: string | undefined): string => {
    if (!tz) return 'GMT';
    if (['GMT', 'EST', 'CST', 'PST', 'IST'].includes(tz)) return tz;
    return IANA_TO_TZ_ABBR[tz] || 'GMT';
};

const mapDTOToFacility = (dto: FacilityDTO, idx: number): Facility => ({
    id: idx,
    name: dto.name || "",
    email: dto.email || "",
    phone: dto.phone || "",
    code: dto.code || "",
    careType: Object.entries(CARE_TYPE_MAP).find(([, v]) => v === dto.admissionType)?.[0] || dto.admissionType || "",
    subDomain: dto.subdomain || "",
    address: dto.address || "",
    city: dto.city || "",
    state: dto.state || "",
    country: dto.country || "",
    postalCode: dto.postalCode || "",
    licenseNumber: dto.licenseNumber || "",
    npiNumber: dto.npiNumber || "",
    medicareProviderNumber: dto.medicareProviderNumber || "",
    medicaidProviderNumber: dto.medicaidProviderNumber || "",
    capacity: dto.capacity !== undefined ? String(dto.capacity) : "",
    notes: dto.notes || "",
    timezone: normalizeTimezone(dto.timezone),
    dateFormat: "DD/MM/YYYY",
    isPrimary: !!dto.isPrimary,
    createdAt: dto.createdAt || new Date().toISOString(),
    logoUrl: dto.logoUrl,
    _apiId: dto.id,
} as any);

// ────────────────────────────────────────────────────────────────────────────────
function parseApiResponse<T>(response: any): T[] {
    if (!response) return [];
    if (Array.isArray(response)) return response;

    const data = response.data !== undefined ? response.data : response;

    if (Array.isArray(data)) return data;
    if (data && typeof data === 'object') {
        if (Array.isArray(data.items)) return data.items;
        if (Array.isArray(data.content)) return data.content;
        if (Array.isArray(data.rules)) return data.rules;
        if (data.id) return [data as T];
    }

    if (response.items && Array.isArray(response.items)) return response.items;
    if (response.content && Array.isArray(response.content)) return response.content;
    if (response.id) return [response as T];

    return [];
}

function isUUID(id: string | null | undefined): boolean {
    if (!id) return false;
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
}

// ─── Empty User Form ────────────────────────────────────────────────────────────
type UserForm = {
    firstName: string; lastName: string; email: string; phone: string; role: string;
    password: string; status: "active" | "inactive"; facilityNames: string[];
    licenseNumber: string; jobTitle: string; profilePictureUrl: string;
};
const emptyForm = (): UserForm => ({
    firstName: "", lastName: "", email: "", phone: "", role: "",
    password: "", status: "active", facilityNames: [],
    licenseNumber: "", jobTitle: "", profilePictureUrl: ""
});

// ─── Sub-component for Facility Logo ──────────────────────────────────────────
const FacilityAvatar = ({ facility }: { facility: Facility }) => {
    const [logoBlob, setLogoBlob] = useState<string | null>(null);

    useEffect(() => {
        let active = true;
        const apiId = (facility as any)._apiId || facility.id;
        if (apiId) {
            facilityService.getFacilityLogoBlob(apiId)
                .then(blobUrl => {
                    if (active && blobUrl) {
                        setLogoBlob(blobUrl);
                    }
                })
                .catch(err => {
                    console.error("Failed to fetch logo for facility", apiId, err);
                });
        }
        return () => { active = false; };
    }, [facility]);

    if (logoBlob) {
        return (
            <Avatar 
                src={logoBlob} 
                alt={facility.name} 
                sx={{ 
                    width: 44, 
                    height: 44, 
                    borderRadius: "12px", 
                    bgcolor: "#FFFFFF", 
                    border: "1px solid #E2E8F0",
                    "& .MuiAvatar-img": { 
                        objectFit: "contain",
                        width: "85%",
                        height: "85%"
                    }
                }} 
            />
        );
    }
    
    return (
        <Avatar sx={{ bgcolor: facility.isPrimary ? "#E8F8FB" : "#F1F5F9", color: facility.isPrimary ? "#48CAE4" : "#64748B", width: 44, height: 44, borderRadius: "12px" }}>
            <OrgIcon />
        </Avatar>
    );
};

// ─── Facilities Tab ─────────────────────────────────────────────────────────────
const FacilitiesTab: React.FC = () => {
    const { user } = useAuth();
    const { currentTenantId, setCurrentTenantId, setTenant } = useTenant();
    const isFacilityAdmin = user?.role === 'FACILITY_ADMIN';
    const [facilities, setFacilities] = useState<Facility[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingFac, setEditingFac] = useState<Facility | null>(null);
    const [deleteFac, setDeleteFac] = useState<Facility | null>(null);
    const [confirmPrimary, setConfirmPrimary] = useState<Facility | null>(null);
    const [form, setForm] = useState<Omit<Facility, "id">>(emptyFacility());
    const [formErr, setFormErr] = useState("");
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [hideCurrentLogo, setHideCurrentLogo] = useState(false);
    const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" as "success" | "error" });
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [page, setPage] = useState(1);
    const [rowsPerPage] = useState(10);
    const [careTypes, setCareTypes] = useState<{ id: string; valueName: string }[]>([]);
    const [fetchedLogoBlobUrl, setFetchedLogoBlobUrl] = useState<string | null>(null);

    // Fetch the logo blob when editing an existing facility
    useEffect(() => {
        let active = true;
        if (dialogOpen && editingFac && (editingFac as any)._apiId) {
            facilityService.getFacilityLogoBlob((editingFac as any)._apiId)
                .then(blobUrl => {
                    if (active && blobUrl) {
                        setFetchedLogoBlobUrl(blobUrl);
                    }
                })
                .catch(err => console.error("Failed to fetch logo blob", err));
        } else {
            setFetchedLogoBlobUrl(null);
        }
        return () => { active = false; };
    }, [dialogOpen, editingFac]);

    // Fetch care types from API
    useEffect(() => {
        const fetchCareTypes = async () => {
            try {
                const items = await masterDataService.getActiveLookupValuesByName("admissionType");
                setCareTypes(items);
            } catch (e) {
                console.error("Failed to fetch care types", e);
            }
        };
        fetchCareTypes();
    }, []);

    const paginatedFacilities = useMemo(() => {
        const start = (page - 1) * rowsPerPage;
        return facilities.slice(start, start + rowsPerPage);
    }, [facilities, page, rowsPerPage]);

    const totalPages = Math.max(1, Math.ceil(facilities.length / rowsPerPage));

    // Load real facilities on mount
    useEffect(() => {
        const fetchFacilities = async () => {
            setLoading(true);
            try {
                const userRole = user?.role?.toUpperCase();
                const isAdminOrSuperAdmin = userRole === 'ADMIN' || userRole === 'SUPER_ADMIN';

                if (isAdminOrSuperAdmin) {
                    // Admin / Super Admin: fetch ALL facilities via role-based access
                    const allFacilitiesPage = await facilityService.getAllFacilities(0, 100);
                    const facilityList: FacilityDTO[] =
                        (allFacilitiesPage as any)?.content ??
                        (allFacilitiesPage as any)?.items ??
                        (Array.isArray(allFacilitiesPage) ? allFacilitiesPage : []);
                    const mapped = facilityList.map((dto, idx) => mapDTOToFacility(dto, idx));
                    mapped.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
                    setFacilities(mapped);
                } else if (user?.id) {
                    // Other roles: fetch only facilities assigned to the user
                    let targetUserId = user.id;
                    try {
                        const dbUser = await userService.getUserById(user.id);
                        if (dbUser && dbUser.id) {
                            targetUserId = dbUser.id;
                        }
                    } catch (e) {
                        console.warn("Could not fetch internal DB user, falling back to context user.id", e);
                    }

                    const response = await facilityService.getFacilitiesByUser(targetUserId);
                    const facilityList = parseApiResponse<FacilityDTO>(response) || [];
                    const mapped = facilityList.map((dto, idx) => mapDTOToFacility(dto, idx));
                    mapped.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
                    setFacilities(mapped);
                } else {
                    setFacilities([]);
                }
            } catch (err) {
                console.error("Failed to load facilities", err);
                setFacilities(SAMPLE_FACILITIES);
                setSnack({ open: true, msg: "Failed to load facilities. Displaying sample data.", sev: "error" });
            } finally {
                setLoading(false);
            }
        };
        fetchFacilities();
    }, [user?.id, user?.role]);


    const openAdd = () => { setEditingFac(null); setForm(emptyFacility()); setLogoFile(null); setHideCurrentLogo(false); setFormErr(""); setFieldErrors({}); setPage(1); setDialogOpen(true); };
    const openEdit = (f: Facility) => {
        setEditingFac(f);
        setLogoFile(null);
        setHideCurrentLogo(false);
        setForm({
            name: f.name, email: f.email, phone: f.phone, code: f.code || "",
            careType: f.careType || "", subDomain: f.subDomain || "", address: f.address,
            city: f.city || "", state: f.state || "", country: f.country || "",
            postalCode: f.postalCode || "", licenseNumber: f.licenseNumber || "",
            npiNumber: f.npiNumber || "", medicareProviderNumber: f.medicareProviderNumber || "",
            medicaidProviderNumber: f.medicaidProviderNumber || "", capacity: f.capacity || "",
            notes: f.notes || "",
            timezone: f.timezone, dateFormat: f.dateFormat,
            createdAt: f.createdAt,
            isPrimary: !!f.isPrimary
        });
        setFormErr(""); setFieldErrors({}); setDialogOpen(true);
    };

    const handleSave = async () => {
        const errors: Record<string, string> = {};

        // Facility Name: Alphabets only
        if (!form.name.trim()) {
            errors.name = "Facility Name is required";
        } else if (!/^[A-Za-z\s]+$/.test(form.name)) {
            errors.name = "Please enter only alphabet characters";
        }

        // Email: Format check
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!form.email.trim()) {
            errors.email = "Contact Email is required";
        } else if (!emailRegex.test(form.email)) {
            errors.email = "Please enter a valid email address.";
        } else {
            const duplicateEmail = facilities.find(f => f.email && f.email.toLowerCase() === form.email.trim().toLowerCase() && (!editingFac || f.id !== editingFac.id));
            if (duplicateEmail) {
                errors.email = "This email already exists.";
            }
        }

        // Phone: Uniqueness and Length check
        if (!form.phone.trim()) {
            errors.phone = "Contact Phone is required";
        } else if (form.phone.length !== 10) {
            errors.phone = "Phone number must be exactly 10 digits";
        } else {
            const duplicatePhone = facilities.find(f => f.phone === form.phone && (!editingFac || f.id !== editingFac.id));
            if (duplicatePhone) {
                errors.phone = "Contact phone number already exists.";
            }
        }

        // Code: Uniqueness check
        if (form.code.trim()) {
            if (!/^\d+$/.test(form.code)) {
                errors.code = "Code must contain only digits";
            } else {
                const duplicateCode = facilities.find(
                    f => f.code === form.code && (!editingFac || f.id !== editingFac.id)
                );

                if (duplicateCode) {
                    errors.code = "Code must be unique";
                }
            }
        }

        if (!form.careType.trim()) {
            errors.careType = "Care Type is required";
        }

        if (form.city?.trim() && /^[^A-Za-z\s]+$/.test(form.city)) {
            errors.city = "Please enter only alphabet characters";
        }

        if (form.state?.trim() && /^[^A-Za-z\s]+$/.test(form.state)) {
            errors.state = "Please enter only alphabet characters";
        }

        if (!form.postalCode?.trim()) {
            errors.postalCode = "Postal Code is required";
        } else if (!/^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{1,10}$/.test(form.postalCode)) {
            errors.postalCode = "Postal code must contain at least one letter and one number";
        }

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            return;
        }

        setSaving(true);
        setFormErr("");
        setFieldErrors({});
        try {
            const clientId = getClientIdFromToken();
            // Send the selected care type directly. It's either:
            //  1. A raw enum value from master data (e.g. "ALF", "HOSPICE")
            //  2. A display label from the old hardcoded fallback list — map it via CARE_TYPE_MAP
            // Do NOT fall back to SNF, as that silently corrupts the selected value.
            const apiCareType: string = CARE_TYPE_MAP[form.careType] || form.careType;
            const payload: any = {
                name: form.name,
                code: form.code,
                clientId,
                admissionType: apiCareType,
                email: form.email,
                phone: form.phone,
                address: form.address || "",
                city: form.city || "",
                state: form.state || "",
                country: form.country || "US",
                postalCode: form.postalCode || "",
                subdomain: form.subDomain || undefined,
                licenseNumber: form.licenseNumber || undefined,
                npiNumber: form.npiNumber || undefined,
                medicareProviderNumber: form.medicareProviderNumber || undefined,
                medicaidProviderNumber: form.medicaidProviderNumber || undefined,
                capacity: form.capacity ? Number(form.capacity) : undefined,
                timezone: form.timezone || undefined,
                notes: form.notes || undefined,
                isPrimary: form.isPrimary,
            };

            let finalFacility: any;
            if (editingFac && (editingFac as any)._apiId) {
                finalFacility = await facilityService.updateFacility((editingFac as any)._apiId, payload);
            } else {
                finalFacility = await facilityService.createFacility(payload);
            }

            // Upload logo if selected
            if (logoFile && finalFacility && finalFacility.id) {
                try {
                    const uploadResult = await facilityService.uploadFacilityLogo(finalFacility.id, logoFile);
                    if (uploadResult && uploadResult.logoUrl) {
                        finalFacility.logoUrl = uploadResult.logoUrl;
                    }
                } catch (logoErr) {
                    console.error("Failed to upload facility logo:", logoErr);
                    // continue with facility save process even if logo fails
                }
            }

            // Sync Primary state ONLY if it was changed in the form
            if (finalFacility && finalFacility.id && form.isPrimary !== (editingFac?.isPrimary ?? false)) {
                await facilityService.setFacilityAsPrimary(finalFacility.id, form.isPrimary);
            }

            // Update local state ONLY after all API calls succeed
            if (editingFac) {
                setFacilities(prev => prev.map(f => {
                    if (f.id === editingFac.id) {
                        return mapDTOToFacility(finalFacility, f.id);
                    }
                    // If the updated facility became primary, other facilities must lose their primary status
                    if (form.isPrimary) {
                        return { ...f, isPrimary: false };
                    }
                    return f;
                }));
                setSnack({ open: true, msg: "Facility updated successfully.", sev: "success" });
            } else {
                const newFac = mapDTOToFacility(finalFacility, Date.now());
                setFacilities(prev => [
                    newFac,
                    ...prev.map(f => ({ ...f, isPrimary: form.isPrimary ? false : f.isPrimary }))
                ]);
                setSnack({ open: true, msg: "Facility added successfully.", sev: "success" });
            }

            setDialogOpen(false);
        } catch (err: any) {
            const msg = err?.response?.data?.message || err?.message || "Failed to save facility. Please try again.";
            const isEmailError = msg.toLowerCase().includes("email must be unique");
            const isPostalError =
                msg.toLowerCase().includes("postal") ||
                msg.toLowerCase().includes("input validation failed");
            if (isEmailError) {
                setFieldErrors(prev => ({ ...prev, email: "This email already exists." }));
                setFormErr("");
            } else if (isPostalError) {
                setFieldErrors(prev => ({ ...prev, postalCode: "Postal code must contain at least one letter and one number (e.g. MH411001)" }));
                setFormErr("");
            } else {
                setFormErr(msg);
            }
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteFac) return;
        if (deleteFac.isPrimary) { setSnack({ open: true, msg: "Cannot delete the primary facility.", sev: "error" }); setDeleteFac(null); return; }
        try {
            const apiId = (deleteFac as any)._apiId;
            if (apiId) await facilityService.deleteFacility(apiId);
            setFacilities(prev => prev.filter(f => f.id !== deleteFac.id));
            setSnack({ open: true, msg: `${deleteFac.name} removed.`, sev: "success" });
        } catch (err: any) {
            setSnack({ open: true, msg: err?.response?.data?.message || "Failed to delete facility.", sev: "error" });
        }
        setDeleteFac(null);
    };

    const handleConfirmPrimary = async () => {
        if (!confirmPrimary) return;
        const apiId = (confirmPrimary as any)?._apiId;
        if (!apiId) {
            setSnack({ open: true, msg: "Cannot set primary: facility not synced to server.", sev: "error" });
            setConfirmPrimary(null);
            return;
        }

        try {
            await facilityService.setFacilityAsPrimary(apiId, true);

            // Update global tenant context
            setCurrentTenantId(apiId);
            setTenant({
                id: apiId,
                name: confirmPrimary.name,
                subdomain: confirmPrimary.subDomain,
                // Add other properties if needed
            } as any);
            localStorage.setItem('currentTenantId', apiId);

            // Update local state
            setFacilities(prev => prev.map(f => ({
                ...f,
                isPrimary: (f as any)._apiId === apiId
            })));

            setSnack({ open: true, msg: "Primary facility updated and selected successfully.", sev: "success" });
        } catch (err: any) {
            const msg = err?.response?.data?.message || err?.message || "Failed to set primary facility.";
            setSnack({ open: true, msg, sev: "error" });
        } finally {
            setConfirmPrimary(null);
        }
    };

    return (
        <>
            <Snackbar open={snack.open} autoHideDuration={3500} onClose={() => setSnack(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
                <Alert severity={snack.sev} sx={{ borderRadius: "12px", fontWeight: 600 }}>{snack.msg}</Alert>
            </Snackbar>

            <SectionCard
                title="Facilities"
                subtitle="Manage all care facilities under this organization"
                icon={<OrgIcon fontSize="small" />}
                iconColor="#48CAE4"
                iconBg="#E8F8FB"
                action={
                    !isFacilityAdmin && (
                        <Button
                            variant="contained"
                            startIcon={<DomainAddIcon sx={{ color: "#000000" }} />}
                            onClick={openAdd}
                            sx={{
                                borderRadius: "10px",
                                textTransform: "none",
                                fontWeight: "normal",
                                bgcolor: "#48CAE4",
                                color: "#000000",
                                "&:hover": { bgcolor: "#3ab3cc" },
                                px: 2.5,
                                width: { xs: "100%", sm: "auto" }
                            }}
                        >
                            Add Facility
                        </Button>
                    )
                }
            >
                <Stack spacing={2}>
                    {paginatedFacilities.map(f => (
                        <Box key={f.id} sx={{
                            p: 2.5, borderRadius: "16px",
                            border: `2px solid ${f.isPrimary ? "#0077B620" : BORDER}`,
                            bgcolor: f.isPrimary ? "#F0F9FF" : "#FAFBFC",
                            transition: "all 0.2s",
                            "&:hover": { boxShadow: "0 4px 20px rgba(0,119,182,0.1)", borderColor: "#0077B630" },
                        }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1.5, mb: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                    <FacilityAvatar facility={f} />
                                    <Box>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <Typography fontWeight={500} sx={{ color: TEXT_MAIN, fontSize: "13px" }}>{f.name}</Typography>
                                            {f.isPrimary && (
                                                <Chip icon={<StarIcon sx={{ fontSize: "11px !important" }} />} label="Primary" size="small" sx={{ bgcolor: "#FEF3C7", color: "#D97706", fontWeight: 700, fontSize: "0.68rem", borderRadius: "8px", "& .MuiChip-icon": { color: "#D97706" } }} />
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                    {!f.isPrimary && !isFacilityAdmin && (
                                        <Tooltip title="Set as Primary">
                                            <Button size="small" variant="outlined" onClick={() => setConfirmPrimary(f)} sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 700, fontSize: "0.72rem", borderColor: "#D97706", color: "#D97706", "&:hover": { bgcolor: "#FEF3C7", borderColor: "#D97706" }, height: 32 }}>
                                                Set Primary
                                            </Button>
                                        </Tooltip>
                                    )}
                                    <Tooltip title="Edit">
                                        <IconButton size="small" onClick={() => openEdit(f)} sx={{ bgcolor: "#E8F8FB", color: "#48CAE4", borderRadius: "8px", width: 32, height: 32 }}>
                                            <EditIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </Tooltip>
                                    {!isFacilityAdmin && (
                                        <Tooltip title={f.isPrimary ? "Cannot delete primary" : "Delete"}>
                                            <span style={{ display: "flex" }}>
                                                <IconButton size="small" onClick={() => setDeleteFac(f)} disabled={f.isPrimary} sx={{ bgcolor: f.isPrimary ? "#F1F5F9" : "#FEE2E2", color: f.isPrimary ? "#CBD5E1" : "#DC2626", borderRadius: "8px", width: 32, height: 32 }}>
                                                    <DeleteIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    )}
                                </Box>
                            </Box>
                            <Grid container spacing={1.5}>
                                {[
                                    { icon: <EmailIcon sx={{ fontSize: 13 }} />, value: f.email },
                                    { icon: <PhoneIcon sx={{ fontSize: 13 }} />, value: f.phone },
                                    { icon: <LocationIcon sx={{ fontSize: 13 }} />, value: f.address },
                                    { icon: <TimezoneIcon sx={{ fontSize: 13 }} />, value: `${f.timezone} · ${f.dateFormat}` },
                                ].map((item, i) => (
                                    <Grid item xs={12} sm={6} key={i}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <Box sx={{ color: "#94A3B8" }}>{item.icon}</Box>
                                            <Typography variant="caption" sx={{ color: TEXT_SUB }}>{item.value}</Typography>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    ))}
                </Stack>

                {totalPages > 1 && (
                    <Box sx={{ mt: 4, display: "flex", justifyContent: "center" }}>
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={(_, p) => setPage(p)}
                            color="primary"
                            shape="rounded"
                            size="medium"
                        />
                    </Box>
                )}
            </SectionCard>

            {/* Add/Edit Dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "20px", overflow: "hidden" } }}>
                <DialogTitle sx={{ bgcolor: "#48CAE4", color: "#000000", display: "flex", alignItems: "center", gap: 1.5, py: 2.5, px: 3 }}>
                    <Avatar sx={{ bgcolor: "rgba(0, 0, 0, 0.08)", color: "#000000", borderRadius: "10px", width: 38, height: 38 }}>
                        {editingFac ? <EditIcon /> : <DomainAddIcon />}
                    </Avatar>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                        <Typography fontWeight={800} fontSize="1rem" sx={{ lineHeight: 1.2 }}>{editingFac ? "Edit Facility" : "Add New Facility"}</Typography>
                        <Typography variant="caption" sx={{ color: "rgba(0, 0, 0, 0.7)", lineHeight: 1.2 }}>
                            {editingFac ? "Update facility information" : "Register a new care facility"}
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ pt: 3, pb: 1 }}>
                    {formErr && <Alert severity="error" sx={{ mb: 2, borderRadius: "10px" }}>{formErr}</Alert>}
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField required fullWidth size="small" label="Facility Name"
                                value={form.name}
                                onChange={e => {
                                    const val = e.target.value.replace(/[^A-Za-z\s]/g, "");
                                    setForm(f => ({ ...f, name: val }));
                                    if (fieldErrors.name) setFieldErrors(prev => { const n = { ...prev }; delete n.name; return n; });
                                }}
                                error={!!fieldErrors.name}
                                helperText={fieldErrors.name}
                                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" }, "& .MuiFormLabel-asterisk": { color: "#d32f2f" } }} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField required fullWidth size="small" label="Contact Email"
                                value={form.email}
                                onChange={e => {
                                    const val = e.target.value;
                                    setForm(f => ({ ...f, email: val }));
                                    setFieldErrors(prev => {
                                        const n = { ...prev };
                                        delete n.email;
                                        if (val.trim()) {
                                            const duplicateEmail = facilities.find(f => f.email && f.email.toLowerCase() === val.trim().toLowerCase() && (!editingFac || f.id !== editingFac.id));
                                            if (duplicateEmail) {
                                                n.email = "This email already exists.";
                                            }
                                        }
                                        return n;
                                    });
                                }}
                                error={!!fieldErrors.email}
                                helperText={fieldErrors.email}
                                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" }, "& .MuiFormLabel-asterisk": { color: "#d32f2f" } }} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField required fullWidth size="small" label="Contact Phone"
                                value={form.phone}
                                onChange={e => {
                                    const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                                    setForm(f => ({ ...f, phone: val }));
                                    if (fieldErrors.phone) setFieldErrors(prev => { const n = { ...prev }; delete n.phone; return n; });
                                }}
                                error={!!fieldErrors.phone}
                                helperText={fieldErrors.phone}
                                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" }, "& .MuiFormLabel-asterisk": { color: "#d32f2f" } }} />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <FormControl required fullWidth size="small">
                                <InputLabel sx={{ "& .MuiFormLabel-asterisk": { color: "#d32f2f" } }}>Care Type</InputLabel>
                                <Select value={form.careType} label="Care Type *" onChange={e => setForm(f => ({ ...f, careType: e.target.value as string }))} sx={{ borderRadius: "10px" }}>
                                    {careTypes.length > 0
                                        ? careTypes.map(ct => (
                                            <MenuItem key={ct.id} value={ct.valueName}>{ct.valueName}</MenuItem>
                                        ))
                                        : [
                                            <MenuItem key="Hospice Care" value="Hospice Care">Hospice Care</MenuItem>,
                                            <MenuItem key="Assisted Living Facility" value="Assisted Living Facility">Assisted Living Facility</MenuItem>,
                                            <MenuItem key="ALF with Hospice" value="ALF with Hospice">ALF with Hospice</MenuItem>,
                                        ]
                                    }
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="Sub Domain" value={form.subDomain} onChange={e => setForm(f => ({ ...f, subDomain: e.target.value }))} sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="NPI Number" value={form.npiNumber} onChange={e => setForm(f => ({ ...f, npiNumber: e.target.value }))} sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" label="Address" value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="City"
                                value={form.city}
                                onChange={e => {
                                    const val = e.target.value.replace(/[^A-Za-z\s]/g, "");
                                    setForm(f => ({ ...f, city: val }));

                                    if (fieldErrors.city) {
                                        setFieldErrors(prev => {
                                            const n = { ...prev };
                                            delete n.city;
                                            return n;
                                        });
                                    }
                                }}
                                error={!!fieldErrors.city}
                                helperText={fieldErrors.city}
                                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="State"
                                value={form.state}
                                onChange={e => {
                                    const val = e.target.value.replace(/[^A-Za-z\s]/g, ""); // ✅ allow only letters + space
                                    setForm(f => ({ ...f, state: val }));

                                    if (fieldErrors.state) {
                                        setFieldErrors(prev => {
                                            const n = { ...prev };
                                            delete n.state;
                                            return n;
                                        });
                                    }
                                }}
                                error={!!fieldErrors.state}
                                helperText={fieldErrors.state}
                                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="Country" value={form.country} onChange={e => setForm(f => ({ ...f, country: e.target.value }))} sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField required fullWidth size="small" label="Postal Code"
                                value={form.postalCode}
                                onChange={e => {
                                    const val = e.target.value.replace(/[^a-zA-Z0-9]/g, "").slice(0, 10);
                                    setForm(f => ({ ...f, postalCode: val }));
                                    if (fieldErrors.postalCode) setFieldErrors(prev => { const n = { ...prev }; delete n.postalCode; return n; });
                                }}
                                inputProps={{ maxLength: 10 }}
                                error={!!fieldErrors.postalCode}
                                helperText={fieldErrors.postalCode}
                                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" }, "& .MuiFormLabel-asterisk": { color: "#d32f2f" } }} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="License Number" value={form.licenseNumber} onChange={e => setForm(f => ({ ...f, licenseNumber: e.target.value }))} sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="Medicare Provider Number" value={form.medicareProviderNumber} onChange={e => setForm(f => ({ ...f, medicareProviderNumber: e.target.value }))} sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="Medicaid Provider Number" value={form.medicaidProviderNumber} onChange={e => setForm(f => ({ ...f, medicaidProviderNumber: e.target.value }))} sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" type="number" label="Capacity" value={form.capacity} onChange={e => setForm(f => ({ ...f, capacity: e.target.value }))} sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Timezone</InputLabel>
                                <Select value={form.timezone} label="Timezone" onChange={e => setForm(f => ({ ...f, timezone: e.target.value as string }))} sx={{ borderRadius: "10px" }}>
                                    <MenuItem value="GMT">🌍 GMT — London</MenuItem>
                                    <MenuItem value="EST">🌎 EST — New York</MenuItem>
                                    <MenuItem value="CST">🌎 CST — Chicago</MenuItem>
                                    <MenuItem value="PST">🌎 PST — Los Angeles</MenuItem>
                                    <MenuItem value="IST">🌏 IST — Mumbai</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Is Primary</InputLabel>
                                <Select
                                    value={form.isPrimary ? "Yes" : "No"}
                                    label="Is Primary"
                                    onChange={e => setForm(f => ({ ...f, isPrimary: e.target.value === "Yes" }))}
                                    sx={{ borderRadius: "10px" }}
                                >
                                    <MenuItem value="Yes">Yes</MenuItem>
                                    <MenuItem value="No">No</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="body2" fontWeight={600} sx={{ color: TEXT_MAIN, mb: 1 }}>Upload Logo</Typography>

                            {/* Hidden native file input — triggered by label clicks */}
                            <input
                                id="facility-logo-upload"
                                type="file"
                                accept="image/png, image/jpeg, image/jpg"
                                style={{ display: 'none' }}
                                onChange={(e) => {
                                    if (e.target.files && e.target.files.length > 0) {
                                        const file = e.target.files[0];
                                        if (file.size > 2 * 1024 * 1024) {
                                            setFormErr('Maximum file size is 2 MB.');
                                            e.target.value = '';
                                            setLogoFile(null);
                                        } else {
                                            setFormErr('');
                                            setLogoFile(file);
                                        }
                                    } else {
                                        setLogoFile(null);
                                    }
                                }}
                            />

                            {/* STATE A — logo is loaded (existing or newly selected) */}
                            {((editingFac && ((editingFac as any).logoUrl || fetchedLogoBlobUrl) && !hideCurrentLogo) || logoFile) ? (
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    px: 2,
                                    py: 1.5,
                                    border: '1.5px solid #E2E8F0',
                                    borderRadius: '10px',
                                    bgcolor: '#FFFFFF',
                                    boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                                }}>
                                    {/* Thumbnail */}
                                    <Box sx={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: '8px',
                                        border: '1px solid #E2E8F0',
                                        bgcolor: '#F8FAFC',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        overflow: 'hidden',
                                        flexShrink: 0,
                                    }}>
                                        <img
                                            src={logoFile ? URL.createObjectURL(logoFile) : (fetchedLogoBlobUrl || (editingFac as any).logoUrl)}
                                            alt="logo preview"
                                            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                        />
                                    </Box>

                                    {/* Name + size */}
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="body2" fontWeight={600} sx={{ color: TEXT_MAIN }} noWrap>
                                            {logoFile ? logoFile.name : 'Current Logo'}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: TEXT_SUB }}>
                                            {logoFile ? `${(logoFile.size / 1024).toFixed(1)} KB` : 'Tap × to remove or click Change to replace'}
                                        </Typography>
                                    </Box>

                                    {/* Change button */}
                                    <label htmlFor="facility-logo-upload" style={{ cursor: 'pointer', flexShrink: 0 }}>
                                        <Box component="span" sx={{
                                            px: 1.5, py: 0.6,
                                            border: '1px solid #CBD5E1',
                                            borderRadius: '6px',
                                            fontSize: '12px',
                                            fontWeight: 600,
                                            color: TEXT_MAIN,
                                            bgcolor: '#F1F5F9',
                                            '&:hover': { bgcolor: '#E2E8F0' },
                                            display: 'inline-block',
                                            lineHeight: '20px',
                                            userSelect: 'none',
                                        }}>
                                            Change
                                        </Box>
                                    </label>

                                    {/* Remove ✕ */}
                                    <Tooltip title="Remove logo">
                                        <IconButton
                                            size="small"
                                            onClick={async () => {
                                                if (logoFile) {
                                                    setLogoFile(null);
                                                    const el = document.getElementById('facility-logo-upload') as HTMLInputElement;
                                                    if (el) el.value = '';
                                                } else {
                                                    if (editingFac && (editingFac as any)._apiId) {
                                                        try {
                                                            await facilityService.deleteFacilityLogo((editingFac as any)._apiId);
                                                            (editingFac as any).logoUrl = undefined;
                                                            setHideCurrentLogo(true);
                                                            setSnack({ open: true, msg: "Logo removed successfully.", sev: "success" });
                                                        } catch (err: any) {
                                                            setSnack({ open: true, msg: err?.response?.data?.message || "Failed to remove logo.", sev: "error" });
                                                        }
                                                    } else {
                                                        setHideCurrentLogo(true);
                                                    }
                                                }
                                            }}
                                            sx={{
                                                color: '#94A3B8',
                                                flexShrink: 0,
                                                '&:hover': { color: '#DC2626', bgcolor: '#FEE2E2' },
                                            }}
                                        >
                                            <CloseIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            ) : (
                                /* STATE B — no logo, show clean upload trigger */
                                <label htmlFor="facility-logo-upload" style={{ cursor: 'pointer', display: 'block' }}>
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        px: 2,
                                        py: 1.25,
                                        border: '1.5px dashed #CBD5E1',
                                        borderRadius: '10px',
                                        bgcolor: '#F8FAFC',
                                        transition: 'border-color 0.2s, background 0.2s',
                                        '&:hover': { borderColor: '#48CAE4', bgcolor: '#F0FBFE' },
                                    }}>
                                        {/* Icon placeholder */}
                                        <Box sx={{
                                            width: 40,
                                            height: 40,
                                            borderRadius: '8px',
                                            border: '1px solid #E2E8F0',
                                            bgcolor: '#FFFFFF',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                            color: '#94A3B8',
                                            fontSize: 22,
                                        }}>
                                            🖼
                                        </Box>

                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="body2" sx={{ color: TEXT_SUB, fontSize: '13px' }}>
                                                No logo selected
                                            </Typography>
                                        </Box>

                                        <Box sx={{
                                            px: 2, py: 0.75,
                                            borderRadius: '8px',
                                            bgcolor: '#48CAE4',
                                            color: '#000',
                                            fontSize: '13px',
                                            flexShrink: 0,
                                            letterSpacing: '0.01em',
                                            lineHeight: '20px',
                                        }}>
                                            Choose File
                                        </Box>
                                    </Box>
                                </label>
                            )}

                            <Typography variant="caption" sx={{ color: TEXT_SUB, mt: 0.75, display: 'block' }}>
                                Supported formats: PNG, JPEG/JPG. Maximum file size: 2 MB.
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth size="small" label="Notes" multiline minRows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2.5, gap: 1 }}>
                    <Button onClick={() => setDialogOpen(false)} disabled={saving} sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, color: TEXT_SUB }}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ borderRadius: "10px", textTransform: "none", fontWeight: "normal", bgcolor: "#48CAE4", color: "#000000", "&:hover": { bgcolor: "#3ab3cc" }, "&.Mui-disabled": { bgcolor: "rgba(0,0,0,0.12)", color: "rgba(0,0,0,0.26)" }, px: 3, minWidth: 130 }}>
                        {saving ? <CircularProgress size={20} color="inherit" /> : (editingFac ? "Update Facility" : "Add Facility")}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirm */}
            <Dialog open={!!deleteFac} onClose={() => setDeleteFac(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "20px" } }}>
                <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, pb: 1, pt: 3, px: 3 }}>
                    <Avatar sx={{ bgcolor: "#FEE2E2", color: "#DC2626", borderRadius: "10px" }}><DeleteIcon /></Avatar>
                    <Typography fontWeight={800} fontSize="1rem" sx={{ color: TEXT_MAIN }}>Confirm Delete</Typography>
                </DialogTitle>
                <DialogContent sx={{ px: 3, pb: 3, pt: 1 }}>
                    <Typography variant="body2" sx={{ color: "#475569", lineHeight: 1.6 }}>
                        Are you sure you want to delete this facility?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3, pt: 0 }}>
                    <Button onClick={() => setDeleteFac(null)} sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, color: "#475569" }}>Cancel</Button>
                    <Button variant="contained" onClick={handleDelete} sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, bgcolor: "#DC2626", "&:hover": { bgcolor: "#B91C1C" }, px: 3 }}>Delete</Button>
                </DialogActions>
            </Dialog>

            {/* Set Primary Confirm */}
            <Dialog open={!!confirmPrimary} onClose={() => setConfirmPrimary(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "20px" } }}>
                <DialogTitle sx={{ pb: 1, pt: 3, px: 3 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Avatar sx={{ bgcolor: "#E8F8FB", color: "#48CAE4", borderRadius: "10px" }}><StarIcon /></Avatar>
                        <Typography fontWeight={800} sx={{ color: TEXT_MAIN }}>Set as Primary</Typography>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ px: 3 }}>
                    <Typography variant="body2" sx={{ color: TEXT_SUB }}>
                        Are you sure you want to set <span style={{ color: TEXT_MAIN, fontWeight: 500 }}>{confirmPrimary?.name}</span> as the primary facility?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2.5, gap: 1 }}>
                    <Button onClick={() => setConfirmPrimary(null)} sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, color: TEXT_SUB }}>No</Button>
                    <Button variant="contained" onClick={handleConfirmPrimary} sx={{ borderRadius: "10px", textTransform: "none", fontWeight: "normal", bgcolor: "#48CAE4", color: "#000000", "&:hover": { bgcolor: "#3ab3cc" }, px: 3 }}>Yes, Set Primary</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

// ─── Users Tab ──────────────────────────────────────────────────────────────────
const UsersTab: React.FC = () => {
    const { user: currentUser } = useAuth();
    const canResetPassword = ["ADMIN", "SUPER_ADMIN"].includes((currentUser?.role || "").toUpperCase());
    const [users, setUsers] = useState<UserRecord[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [roleFilter, setRoleFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");
    const [page, setPage] = useState(1);
    const [rowsPerPage] = useState(10);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingUser, setEditingUser] = useState<UserRecord | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<UserRecord | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isResetting, setIsResetting] = useState(false);
    const [resetTarget, setResetTarget] = useState<UserRecord | null>(null);
    const [resetUsername, setResetUsername] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showNewPass, setShowNewPass] = useState(false);
    const [showConfirmPass, setShowConfirmPass] = useState(false);
    const [resetError, setResetError] = useState("");
    const [newPwdError, setNewPwdError] = useState("");
    const [confirmPwdError, setConfirmPwdError] = useState("");
    const [form, setForm] = useState<UserForm>(emptyForm());
    const [showPass, setShowPass] = useState(false);
    const [formError, setFormError] = useState("");
    const [formErrorSeverity, setFormErrorSeverity] = useState<"error" | "info">("error");
    const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" as "success" | "success" | "error" });
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [activeFacilities, setActiveFacilities] = useState<FacilityDTO[]>([]);
    const [roles, setRoles] = useState<any[]>([]);
    const [fetchingRoles, setFetchingRoles] = useState(false);
    const [statusList, setStatusList] = useState<any[]>([]);
    const [loginSecurityState, setLoginSecurityState] = useState<Record<string, { failedAttempts: number; locked: boolean }>>({});

    const loadLoginSecurityState = useCallback(() => {
        const loginSecurityKey = 'login_security_state';
        const stateStr = localStorage.getItem(loginSecurityKey) || '{}';
        try {
            setLoginSecurityState(JSON.parse(stateStr));
        } catch (e) {
            setLoginSecurityState({});
        }
    }, []);

    useEffect(() => {
        loadLoginSecurityState();
    }, [loadLoginSecurityState]);

    const handleUnlockUser = (usernameToUnlock: string) => {
        if (!usernameToUnlock) return;
        const loginSecurityKey = 'login_security_state';
        let currentSecurityState: Record<string, { failedAttempts: number; locked: boolean }> = {};
        try {
            currentSecurityState = JSON.parse(localStorage.getItem(loginSecurityKey) || '{}');
        } catch (e) { }

        if (currentSecurityState[usernameToUnlock]) {
            currentSecurityState[usernameToUnlock].locked = false;
            currentSecurityState[usernameToUnlock].failedAttempts = 0;
            localStorage.setItem(loginSecurityKey, JSON.stringify(currentSecurityState));
            loadLoginSecurityState();
            setSnack({ open: true, msg: `User account unlocked successfully.`, sev: "success" });
        }
    };
    const [fetchingStatus, setFetchingStatus] = useState(false);

    useEffect(() => {
        facilityService.getActiveFacilities()
            .then(setActiveFacilities)
            .catch(err => console.error('Failed to fetch active facilities', err));
    }, []);

    const validatePassword = (pwd: string) => {
        if (pwd.length < 8) return "Password must be at least 8 characters";
        if (!/[a-z]/.test(pwd)) return "Password must contain at least one lowercase letter";
        if (!/[A-Z]/.test(pwd)) return "Password must contain at least one uppercase letter";
        if (!/[0-9]/.test(pwd)) return "Password must contain at least one number";
        if (!/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) return "Password must contain at least one special character";
        return null;
    };

    const validateForm = () => {
        const errors: Record<string, string> = {};

        // Names: Alphabets only
        if (!form.firstName.trim()) {
            errors.firstName = "First name is required";
        } else if (!/^[A-Za-z\s\-']+$/.test(form.firstName)) {
            errors.firstName = "Please enter only alphabet characters";
        }

        if (!form.lastName.trim()) {
            errors.lastName = "Last name is required";
        } else if (!/^[A-Za-z\s\-']+$/.test(form.lastName)) {
            errors.lastName = "Please enter only alphabet characters";
        }

        // Email: Strict regex
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!form.email.trim()) {
            errors.email = "Email address is required";
        } else if (!emailRegex.test(form.email)) {
            errors.email = "Please enter a valid email address.";
        }

        // Phone: Exactly 10 digits
        if (form.phone.trim()) {
            if (!/^\d+$/.test(form.phone)) {
                errors.phone = "Only numbers are allowed";
            } else if (form.phone.length !== 10) {
                errors.phone = "Phone number must be exactly 10 digits";
            }
        }

        if (!form.role) {
            errors.role = "Role is required";
        }

        if (!editingUser) {
            if (!form.password.trim()) {
                errors.password = "New password is required";
            } else {
                const pwdError = validatePassword(form.password.trim());
                if (pwdError) {
                    errors.password = pwdError;
                }
            }
        } else if (editingUser && form.password.trim()) {
            const pwdError = validatePassword(form.password.trim());
            if (pwdError) {
                errors.password = pwdError;
            }
        }

        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const mapDTOToRecord = useCallback((dto: UserDTO): UserRecord => {
        const firstName = dto.firstName || "";
        const lastName = dto.lastName || "";
        const initials = (firstName[0] || "") + (lastName[0] || "");
        const colors = ["#DC2626", "#7C3AED", "#0077B6", "#16A34A", "#D97706", "#059669"];

        // Deterministic color based on id string
        const idStr = String(dto.id || dto.email || "");
        const colorIndex = idStr.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;

        const rawRole = (dto.role || (dto.roles && dto.roles.length > 0 ? dto.roles[0] : "NURSE")).toUpperCase();
        const displayRole = rawRole === "NURSE" ? "CAREGIVER" : rawRole;

        return {
            id: dto.id || Date.now(),
            name: `${firstName} ${lastName}`.trim() || dto.username || "Unknown",
            email: dto.email,
            username: dto.username || dto.email,
            role: displayRole,
            status: dto.isActive !== false ? "active" : "inactive",
            lastLogin: (dto.lastLogin || dto.lastLoginAt)
                ? new Date((dto.lastLogin || dto.lastLoginAt) as string).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                : "Never",
            phone: dto.phone || "N/A",
            initials: initials.toUpperCase() || "??",
            avatarColor: colors[colorIndex],
            facilityNames: (dto.facilityNames || dto.facility?.map((f: any) => f.name) || []).map(f => f.replace(/_/g, ' ')),
            licenseNumber: dto.licenseNumber || "",
            jobTitle: dto.jobTitle || "",
            profilePictureUrl: dto.profilePictureUrl || "",
            deleted: (dto as any).deleted || (dto as any).isDeleted || false
        };
    }, []);

    const fetchRoles = useCallback(async () => {
        setFetchingRoles(true);
        try {
            const data = await masterDataService.getActiveLookupValuesByName('Role');
            setRoles(data || []);
        } catch (error) {
            console.error("Error fetching roles:", error);
            setSnack({ open: true, msg: "Failed to load roles from API.", sev: "error" });
        } finally {
            setFetchingRoles(false);
        }
    }, []);

    const fetchStatus = async () => {
        setFetchingStatus(true);
        try {
            const data = await masterDataService.getActiveLookupValuesByName('Status');
            setStatusList(data || []);
        } catch (error) {
            console.error("Error fetching status:", error);
            setSnack({ open: true, msg: "Failed to load status options from API.", sev: "error" });
        } finally {
            setFetchingStatus(false);
        }
    };

    const fetchUsers = useCallback(async (searchQuery: string = '') => {
        try {
            setLoading(true);
            const response = await userService.getAllUsers(0, 100, searchQuery);

            // Handle both Pageable response { content: [] } and raw array []
            const rawData = Array.isArray(response) ? response : (response && (response as any).content ? (response as any).content : []);

            const mappedUsers = rawData.map(mapDTOToRecord);
            setUsers(mappedUsers);
        } catch (error: any) {
            console.error("Error fetching users:", error);
            setSnack({ open: true, msg: "Failed to load users from API.", sev: "error" });
        } finally {
            setLoading(false);
        }
    }, [mapDTOToRecord]);

    useEffect(() => {
        fetchRoles();
    }, [fetchRoles]);

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchUsers(search);
        }, 500);
        return () => clearTimeout(timer);
    }, [fetchUsers, search]);

    useEffect(() => {
        setPage(1);
    }, [search, roleFilter, statusFilter]);

    const filtered = useMemo(() => users.filter(u => {
        const matchQ = true; // Handled by backend search API
        const matchR = roleFilter === "all" ||
            (roleFilter === "ADMIN_GROUP" ? (u.role === "ADMIN" || u.role === "SUPER_ADMIN" || u.role === "CLIENT_ADMIN") : u.role === roleFilter);

        let matchS = false;
        if (statusFilter === "all") {
            matchS = !u.deleted;
        } else if (statusFilter === "deleted") {
            matchS = !!u.deleted;
        } else {
            matchS = !u.deleted && u.status === statusFilter;
        }

        return matchQ && matchR && matchS;
    }), [users, search, roleFilter, statusFilter]);

    const paginated = useMemo(() => {
        const start = (page - 1) * rowsPerPage;
        return filtered.slice(start, start + rowsPerPage);
    }, [filtered, page, rowsPerPage]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));

    const openAdd = () => {
        fetchRoles();
        fetchStatus();
        setEditingUser(null);
        setForm(emptyForm());
        setFormError("");
        setFormErrorSeverity("error");
        setFieldErrors({});
        setDialogOpen(true);
    };
    const openEdit = (u: UserRecord) => {
        fetchRoles();
        fetchStatus();
        setEditingUser(u);
        setFieldErrors({});
        const nameParts = u.name.split(" ");
        let firstName = nameParts[0] || "";
        let lastName = nameParts.slice(1).join(" ") || "";
        let email = u.email || "";


        if (firstName.includes("@") && !email) {
            email = firstName;
            firstName = "";
        }

        let facilityNames: string[] = [];
        if (Array.isArray(u.facilityNames)) {
            facilityNames = u.facilityNames;
        } else if ((u as any).facilityName) {
            facilityNames = [(u as any).facilityName];
        }

        const phone = (u.phone === "N/A" || u.phone === "NA") ? "" : u.phone;

        setForm({
            firstName, lastName, email, phone, role: u.role ? u.role.toUpperCase().replace(/\s+/g, '_') : "",
            password: "", status: u.status, facilityNames,
            licenseNumber: u.licenseNumber || "",
            jobTitle: u.jobTitle || "",
            profilePictureUrl: u.profilePictureUrl || ""
        });
        setFormError("");
        setFormErrorSeverity("error");
        setDialogOpen(true);
    };

    const handleSave = async () => {
        setFormError("");
        if (!validateForm()) return;

        try {
            const facilityIds = (form.facilityNames || [])
                .map(name => {
                    const found = activeFacilities.find(f => f.name === name);
                    return found ? found.id : null;
                })
                .filter(id => !!id);

            // Map roles for backend. Per curl, Lead Caregiver gets ["NURSE", "LEAD_CAREGIVER"]
            let rolesArray: string[] = [];
            // User requirement: Whatever role is entered should be sent in uppercase with underscores
            let roleToBackend = form.role.toUpperCase().trim().replace(/\s+/g, '_');

            if (form.role === "CAREGIVER") {
                roleToBackend = "NURSE";
                rolesArray = ["NURSE"];
            } else if (form.role === "LEAD_CAREGIVER") {
                rolesArray = ["NURSE", "LEAD_CAREGIVER"];
                roleToBackend = "LEAD_CAREGIVER";
            } else if (form.role === "FACILITY_MANAGER") {
                roleToBackend = "FACILITY_ADMIN";
                rolesArray = ["FACILITY_ADMIN"];
            } else {
                rolesArray = [roleToBackend];
            }

            const department = (form.role === "CAREGIVER" || form.role === "LEAD_CAREGIVER") ? "NURSE" : roleToBackend;

            const payload: any = {
                firstName: form.firstName,
                lastName: form.lastName,
                email: form.email,
                isActive: form.status === "active",
                emailVerified: true,
                phone: (form.phone === "N/A" || form.phone === "NA" || !form.phone.trim()) ? null : form.phone,
                licenseNumber: form.licenseNumber || (editingUser ? "" : "LIC" + Math.floor(Math.random() * 100000)),
                department,
                jobTitle: form.jobTitle,
                profilePictureUrl: form.profilePictureUrl,
                facilityIds,
                role: roleToBackend,
                roles: rolesArray,
            };

            // Only include password if it is not empty
            if (form.password.trim()) {
                payload.password = form.password;
            }

            if (editingUser) {
                const updatedDTO = await userService.updateUser(String(editingUser.id), payload);
                setUsers(prev => prev.map(u => u.id === editingUser.id ? mapDTOToRecord(updatedDTO) : u));
                setSnack({ open: true, msg: "User updated successfully!", sev: "success" });
            } else {
                const newDTO = await userService.createUser({
                    ...payload,
                    username: form.email,
                    password: form.password,
                });
                setUsers(prev => [mapDTOToRecord(newDTO), ...prev]);
                setSnack({ open: true, msg: "User created successfully!", sev: "success" });
            }
            setDialogOpen(false);
        } catch (error: any) {
            const rawMessage = error?.response?.data?.message || error?.response?.data?.title || "Failed to save user.";
            let displayMessage = rawMessage;

            let severity: "error" | "info" = "error";
            if (typeof rawMessage === "string") {
                const normalized = rawMessage.toLowerCase();
                if (normalized.includes("already exists and is active")) {
                    displayMessage = "This user is already active. Please use the existing account instead of creating a new one.";
                    severity = "info";
                } else if (normalized.includes("already exists and is disabled") || normalized.includes("already exists but is disabled")) {
                    displayMessage = "An account with this email already exists and is disabled. Please reactivate it.";
                    severity = "info";
                } else if (
                    normalized.includes("facility ids are mandatory") ||
                    normalized.includes("facility id is mandatory") ||
                    normalized.includes("facility ids are required") ||
                    normalized.includes("facility id is required") ||
                    normalized.includes("facility is mandatory")
                ) {
                    displayMessage = "Please select at least one Facility before saving";
                    severity = "info";
                }
            }

            setFormError(displayMessage);
            setFormErrorSeverity(severity);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setIsDeleting(true);
        try {
            await userService.hardDeleteUser(String(deleteTarget.id));
            setUsers(prev => prev.filter(u => u.id !== deleteTarget.id));
            setSnack({ open: true, msg: `${deleteTarget.name} has been removed.`, sev: "success" });
            setDeleteTarget(null);
        } catch (error: any) {
            console.error("Delete user error:", error);
            const errorMessage = error.response?.data?.message || error.response?.data?.title || "Failed to delete user.";
            setSnack({ open: true, msg: errorMessage, sev: "error" });
        } finally {
            setIsDeleting(false);
        }
    };

    const toggleStatus = async (u: UserRecord) => {
        try {
            const nextActive = u.status !== "active";
            const updatedDTO = await userService.toggleUserStatus(String(u.id), nextActive);
            setUsers(prev => prev.map(x => x.id === u.id ? mapDTOToRecord(updatedDTO) : x));
            setSnack({ open: true, msg: `${u.name} is now ${nextActive ? "active" : "inactive"}.`, sev: "success" });
        } catch (error: any) {
            console.error("Toggle user status error:", error);
            setSnack({ open: true, msg: error.response?.data?.message || "Failed to update user status.", sev: "error" });
        }
    };

    const openReset = (u: UserRecord) => {
        setResetTarget(u);
        setResetUsername(u.email || ""); // pre-fill username with email if available
        setNewPassword("");
        setConfirmPassword("");
        setResetError("");
        setShowNewPass(false);
        setShowConfirmPass(false);
    };
    const handleResetPassword = async () => {
        let hasError = false;
        if (!resetUsername.trim() || !newPassword.trim() || !confirmPassword.trim()) {
            setResetError("Please fill in all fields.");
            hasError = true;
        } else {
            setResetError("");
        }

        const pwdError = validatePassword(newPassword);
        if (pwdError) {
            setNewPwdError(pwdError);
            hasError = true;
        } else {
            setNewPwdError("");
        }

        if (newPassword && confirmPassword && newPassword !== confirmPassword) {
            setConfirmPwdError("Passwords do not match.");
            hasError = true;
        } else {
            setConfirmPwdError("");
        }

        if (hasError || !resetTarget) return;

        setIsResetting(true);
        try {
            await userService.resetPassword(String(resetTarget.id), {
                newPassword: newPassword,
                confirmPassword: confirmPassword
            });
            setSnack({ open: true, msg: `Password reset successfully for ${resetUsername}.`, sev: "success" });
            setResetTarget(null);
            setResetUsername("");
            setNewPassword("");
            setConfirmPassword("");
            setResetError("");
            setNewPwdError("");
            setConfirmPwdError("");
        } catch (error: any) {
            console.error("Failed to reset password", error);
            const msg = error?.response?.data?.message || error?.message || "Failed to reset password. Please try again.";

            if (msg.toLowerCase().includes("permission") || error?.response?.status === 403) {
                setResetError("You do not have Permission to change the password of a Super Admin");
            } else {
                setResetError(msg);
            }
        } finally {
            setIsResetting(false);
        }
    };

    const stats = useMemo(() => ({
        total: users.filter(u => !u.deleted).length,
        active: users.filter(u => !u.deleted && u.status === "active").length,
        admins: users.filter(u => !u.deleted && (u.role === "ADMIN" || u.role === "SUPER_ADMIN" || u.role === "CLIENT_ADMIN")).length,
    }), [users]);

    return (
        <>
            <Snackbar open={snack.open} autoHideDuration={3500}
                onClose={() => setSnack(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}>
                <Alert severity={snack.sev} sx={{ borderRadius: "12px", fontWeight: 600 }}>{snack.msg}</Alert>
            </Snackbar>

            <SectionCard
                title="User Management"
                subtitle="Manage staff accounts, roles, and access permissions"
                icon={<UsersIcon fontSize="small" />}
                iconColor="#48CAE4"
                iconBg="#E8F8FB"
                action={
                    <Button variant="contained" startIcon={<PersonAddIcon />} onClick={openAdd} sx={{
                        borderRadius: "10px", textTransform: "none", fontWeight: "normal",
                        bgcolor: '#48CAE4', color: '#000000', px: 2.5,
                        '&:hover': { bgcolor: '#3ab3cc' }
                    }}>
                        Add User
                    </Button>
                }
            >
                {/* ── Stats Bar ─── */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    {[
                        { label: "Total Users", value: stats.total, color: "#0077B6", bg: "#E0F2FE", filter: "total" },
                        { label: "Active", value: stats.active, color: "#16A34A", bg: "#DCFCE7", filter: "active" },
                        { label: "Administrators", value: stats.admins, color: "#DC2626", bg: "#FEE2E2", filter: "admins" },
                    ].map(s => (
                        <Grid item xs={12} sm={4} key={s.label}>
                            <Box
                                onClick={() => {
                                    if (s.filter === "total") {
                                        setRoleFilter("all");
                                        setStatusFilter("all");
                                    } else if (s.filter === "active") {
                                        setStatusFilter("active");
                                        setRoleFilter("all");
                                    } else if (s.filter === "admins") {
                                        setRoleFilter("ADMIN_GROUP");
                                        setStatusFilter("all");
                                    }
                                    setSearch("");
                                }}
                                sx={{
                                    p: 2,
                                    borderRadius: "14px",
                                    bgcolor: s.bg,
                                    border: `1.5px solid ${s.color}20`,
                                    textAlign: "center",
                                    cursor: "pointer",
                                    transition: "all 0.2s ease-in-out",
                                    "&:hover": {
                                        transform: "translateY(-4px)",
                                        boxShadow: `0 4px 12px ${s.color}25`,
                                        borderColor: s.color,
                                    },
                                    "&:active": {
                                        transform: "translateY(0)",
                                    }
                                }}
                            >
                                <Typography fontWeight={800} sx={{ color: s.color, fontSize: "1.6rem", lineHeight: 1 }}>{s.value}</Typography>
                                <Typography variant="caption" sx={{ color: TEXT_SUB, fontWeight: 600 }}>{s.label}</Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>

                {/* ── Filters ─── */}
                <Box sx={{ display: "flex", gap: 1.5, mb: 2.5, flexWrap: "wrap", alignItems: "center" }}>
                    <TextField size="small" placeholder="Search by name or email" value={search}
                        onChange={e => setSearch(e.target.value)}
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "#94A3B8", fontSize: 18 }} /></InputAdornment> }}
                        sx={{ flex: 1, minWidth: 200, "& .MuiOutlinedInput-root": { borderRadius: "10px", bgcolor: "#FAFBFC" } }} />
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Role</InputLabel>
                        <Select
                            value={roleFilter}
                            onChange={e => setRoleFilter(e.target.value)}
                            label="Role"
                            sx={{ borderRadius: "10px", bgcolor: "#FAFBFC" }}
                            renderValue={(selected) => {
                                if (selected === "ADMIN_GROUP") return "Administrators";
                                if (selected === "all") return "All Roles";
                                const roleFromApi = roles.find(r => r.valueName.toUpperCase().replace(/\s+/g, '_') === selected);
                                const mappedKey = selected === 'FACILITY_MANAGER' ? 'FACILITY_ADMIN' : (selected as string);
                                if (roleFromApi) return ROLE_CONFIG[mappedKey]?.label || roleFromApi.valueName;
                                return ROLE_CONFIG[mappedKey]?.label || (selected as string).replace(/_/g, ' ');
                            }}
                        >
                            <MenuItem value="all">All Roles</MenuItem>
                            {roles.map(role => {
                                const roleKey = role.valueName.toUpperCase().replace(/\s+/g, '_');
                                const mappedKey = roleKey === 'FACILITY_MANAGER' ? 'FACILITY_ADMIN' : roleKey;
                                const cfg = ROLE_CONFIG[mappedKey] || ROLE_CONFIG[role.valueName.toUpperCase()] || { label: role.valueName };
                                return <MenuItem key={role.id || roleKey} value={mappedKey}>{cfg.label}</MenuItem>;
                            })}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                        <InputLabel>Status</InputLabel>
                        <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} label="Status" sx={{ borderRadius: "10px", bgcolor: "#FAFBFC" }}>
                            <MenuItem value="all">All Status</MenuItem>
                            <MenuItem value="active">Active</MenuItem>
                            <MenuItem value="inactive">Inactive</MenuItem>
                            <MenuItem value="deleted">Deleted</MenuItem>
                        </Select>
                    </FormControl>
                    <Tooltip title="Reset Filters">
                        <IconButton size="small" onClick={() => { setSearch(""); setRoleFilter("all"); setStatusFilter("all"); }}
                            sx={{ border: `1px solid ${BORDER}`, borderRadius: "10px", bgcolor: "#FAFBFC" }}>
                            <RefreshIcon fontSize="small" sx={{ color: TEXT_SUB }} />
                        </IconButton>
                    </Tooltip>
                </Box>

                {/* ── User Table ─── */}
                <Box sx={{ overflowX: "auto" }}>
                    <TableContainer sx={{ borderRadius: "14px", border: `1px solid ${BORDER}`, position: "relative", minHeight: loading ? 200 : "auto", minWidth: 800 }}>
                        {loading && (
                            <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "rgba(255,255,255,0.7)", zIndex: 1 }}>
                                <CircularProgress size={32} />
                            </Box>
                        )}
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                                    {["User", "Role", "Status", "Last Login", "Phone", "Actions"].map(h => (
                                        <TableCell key={h} sx={{ fontWeight: 700, color: TEXT_SUB, fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.04em", borderBottom: `1px solid ${BORDER}`, py: 1.5, px: 2 }}>
                                            {h}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginated.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} sx={{ textAlign: "center", py: 5, color: TEXT_SUB }}>
                                            No users found matching your filters.
                                        </TableCell>
                                    </TableRow>
                                ) : paginated.map((u, idx) => (
                                    <TableRow key={u.id} sx={{
                                        bgcolor: idx % 2 === 0 ? "#FFFFFF" : "#FAFBFC",
                                        "&:hover": { bgcolor: "#EFF6FF" },
                                        transition: "background 0.15s",
                                        "&:last-child td": { borderBottom: "none" },
                                    }}>
                                        <TableCell sx={{ py: 1.5, px: 2 }}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                                <Badge badgeContent={u.status === "active" ? "" : null}
                                                    color={u.status === "active" ? "success" : "default"}
                                                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                                                    variant="dot">
                                                    <Avatar sx={{ bgcolor: u.avatarColor + "20", color: u.avatarColor, width: 36, height: 36, fontSize: "0.78rem", fontWeight: 800, borderRadius: "10px" }}>
                                                        {u.initials}
                                                    </Avatar>
                                                </Badge>
                                                <Box>
                                                    <Typography variant="body2" fontWeight={700} sx={{ color: TEXT_MAIN, lineHeight: 1.2 }}>{u.name}</Typography>
                                                    <Typography variant="caption" sx={{ color: TEXT_SUB, display: "block" }}>{u.email}</Typography>
                                                    {u.facilityNames && u.facilityNames.length > 0 && (
                                                        <Typography variant="caption" sx={{ color: PRIMARY, fontWeight: 600 }}>
                                                            {u.facilityNames.join(", ")}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ py: 1.5, px: 2 }}><RoleChip role={u.role} /></TableCell>
                                        <TableCell sx={{ py: 1.5, px: 2 }}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                <StatusChip status={u.status} deleted={u.deleted} />
                                                {loginSecurityState[u.username || u.email]?.locked && (
                                                    <Chip
                                                        label="Locked"
                                                        color="error"
                                                        size="small"
                                                        icon={<LockIcon sx={{ fontSize: '12px' }} />}
                                                        sx={{ height: 20, fontSize: "0.7rem", "& .MuiChip-icon": { ml: 0.5 } }}
                                                    />
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ py: 1.5, px: 2 }}>
                                            <Typography variant="caption" sx={{ color: TEXT_SUB, whiteSpace: "nowrap" }}>{u.lastLogin}</Typography>
                                        </TableCell>
                                        <TableCell sx={{ py: 1.5, px: 2 }}>
                                            <Typography variant="caption" sx={{ color: TEXT_SUB }}>{u.phone}</Typography>
                                        </TableCell>
                                        <TableCell sx={{ py: 1.5, px: 2 }}>
                                            <Box sx={{ display: "flex", gap: 0.8 }}>
                                                <Tooltip title="Edit User">
                                                    <IconButton size="small" onClick={() => openEdit(u)}
                                                        sx={{ bgcolor: "#E0F2FE", color: "#0284C7", borderRadius: "10px", "&:hover": { bgcolor: "#BAE6FD" } }}>
                                                        <EditIcon sx={{ fontSize: 15 }} />
                                                    </IconButton>
                                                </Tooltip>
                                                {loginSecurityState[u.username || u.email]?.locked && (
                                                    <Tooltip title="Unlock Account">
                                                        <IconButton size="small" onClick={() => handleUnlockUser(u.username || u.email)}
                                                            sx={{ bgcolor: "#FEF3C7", color: "#D97706", borderRadius: "10px", "&:hover": { bgcolor: "#FDE68A" } }}>
                                                            <LockOpenIcon sx={{ fontSize: 15 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {canResetPassword && (
                                                    <Tooltip title="Reset Password">
                                                        <IconButton size="small" onClick={() => openReset(u)}
                                                            sx={{ bgcolor: "#EDE9FE", color: "#7C3AED", borderRadius: "10px", "&:hover": { bgcolor: "#DDD6FE" } }}>
                                                            <LockResetIcon sx={{ fontSize: 15 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                <Tooltip title={u.status === "active" ? "Deactivate" : "Activate"}>
                                                    <IconButton size="small" onClick={() => toggleStatus(u)}
                                                        sx={{
                                                            bgcolor: "#FEF3C7",
                                                            color: "#D97706",
                                                            borderRadius: "10px",
                                                            "&:hover": { bgcolor: "#FDE68A" },
                                                        }}>
                                                        {u.status === "active" ? <InactiveIcon sx={{ fontSize: 15 }} /> : <ActiveIcon sx={{ fontSize: 15 }} />}
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete User">
                                                    <IconButton size="small" onClick={() => setDeleteTarget(u)}
                                                        sx={{ bgcolor: "#FEE2E2", color: "#DC2626", borderRadius: "10px", "&:hover": { bgcolor: "#FECACA" } }}>
                                                        <DeleteIcon sx={{ fontSize: 15 }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>

                <Box
                    sx={{
                        mt: 2.5,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <Typography variant="caption" sx={{ color: TEXT_SUB, fontWeight: 600 }}>
                        Showing {paginated.length} of {filtered.length} users
                    </Typography>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(_, v) => setPage(v)}
                        variant="outlined"
                        shape="rounded"
                        size="small"
                        sx={{
                            "& .MuiPaginationItem-root": {
                                borderRadius: "8px",
                                fontWeight: 700,
                                color: TEXT_SUB,
                                borderColor: BORDER,
                                "&.Mui-selected": {
                                    bgcolor: PRIMARY + "15",
                                    color: PRIMARY,
                                    borderColor: PRIMARY + "50",
                                },
                            },
                        }}
                    />
                </Box>
            </SectionCard>

            {/* ── Add / Edit Dialog ─── */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: "20px", overflow: "hidden" } }}>
                <DialogTitle sx={{
                    bgcolor: '#48CAE4',
                    color: "#000000", display: "flex", alignItems: "center", gap: 1.5, py: 2.5, px: 3,
                }}>
                    <Avatar sx={{ bgcolor: "rgba(0,0,0,0.1)", color: "#000000", borderRadius: "10px", width: 38, height: 38 }}>
                        {editingUser ? <EditIcon /> : <PersonAddIcon />}
                    </Avatar>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                        <Typography fontWeight={800} fontSize="1rem" sx={{ color: "#000000", lineHeight: 1.2 }}>{editingUser ? "Edit User" : "Add New User"}</Typography>
                        <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.7)", lineHeight: 1.2 }}>
                            {editingUser ? "Update account details and permissions" : "Create a new staff account"}
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ pt: 3, pb: 1 }}>
                    {formError && <Alert severity={formErrorSeverity} sx={{ mb: 2, borderRadius: "10px" }}>{formError}</Alert>}
                    {form.status === 'inactive' && (
                        <Alert severity="info" sx={{ mb: 2, borderRadius: "10px" }}>
                            This user is inactive. All other fields are disabled until the status is changed to Active.
                        </Alert>
                    )}
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField required fullWidth size="small" label="First Name" value={form.firstName}
                                disabled={form.status === 'inactive'}
                                onChange={e => {
                                    const val = e.target.value.replace(/[^A-Za-z]/g, "");
                                    setForm(f => ({ ...f, firstName: val }));
                                    if (fieldErrors.firstName) setFieldErrors(prev => { const n = { ...prev }; delete n.firstName; return n; });
                                }}
                                error={!!fieldErrors.firstName}
                                helperText={fieldErrors.firstName}
                                autoComplete="given-name"
                                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" }, "& .MuiFormLabel-asterisk": { color: "#d32f2f" } }} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField required fullWidth size="small" label="Last Name" value={form.lastName}
                                disabled={form.status === 'inactive'}
                                onChange={e => {
                                    const val = e.target.value.replace(/[^A-Za-z]/g, "");
                                    setForm(f => ({ ...f, lastName: val }));
                                    if (fieldErrors.lastName) setFieldErrors(prev => { const n = { ...prev }; delete n.lastName; return n; });
                                }}
                                error={!!fieldErrors.lastName}
                                helperText={fieldErrors.lastName}
                                autoComplete="family-name"
                                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" }, "& .MuiFormLabel-asterisk": { color: "#d32f2f" } }} />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField required fullWidth size="small" label="Email Address" value={form.email}
                                disabled={form.status === 'inactive'}
                                onChange={e => {
                                    const val = e.target.value;
                                    setForm(f => ({ ...f, email: val }));
                                    if (fieldErrors.email) setFieldErrors(prev => { const n = { ...prev }; delete n.email; return n; });
                                }}
                                error={!!fieldErrors.email}
                                helperText={fieldErrors.email}
                                autoComplete="email"
                                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" }, "& .MuiFormLabel-asterisk": { color: "#d32f2f" } }} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="Phone Number" value={form.phone}
                                disabled={form.status === 'inactive'}
                                onChange={e => {
                                    const val = e.target.value;
                                    // Strictly allow only numbers for phone as requested
                                    const filtered = val.replace(/\D/g, "").slice(0, 10);
                                    setForm(f => ({ ...f, phone: filtered }));
                                    if (fieldErrors.phone) setFieldErrors(prev => { const n = { ...prev }; delete n.phone; return n; });
                                }}
                                error={!!fieldErrors.phone}
                                helperText={fieldErrors.phone || (form.phone.length > 0 && form.phone.length < 10 ? "Should be 10 digits" : "")}
                                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl required fullWidth size="small" disabled={form.status === 'inactive'}
                                sx={{ "& .MuiFormLabel-asterisk": { color: "#d32f2f" } }}>
                                <InputLabel>Facility Names</InputLabel>
                                <Select
                                    multiple
                                    value={Array.isArray(form.facilityNames) ? form.facilityNames : []}
                                    label="Facility Names *"
                                    onChange={e => {
                                        const { target: { value } } = e;
                                        setForm(f => ({
                                            ...f,
                                            facilityNames: typeof value === "string" ? value.split(",") : value,
                                        }));
                                    }}
                                    renderValue={selected => (
                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                            {(selected || []).map(value => (
                                                <Chip
                                                    key={value}
                                                    label={value}
                                                    size="small"
                                                    onDelete={(e) => {
                                                        e.stopPropagation();
                                                        setForm(f => ({
                                                            ...f,
                                                            facilityNames: (f.facilityNames || []).filter(v => v !== value)
                                                        }));
                                                    }}
                                                    onMouseDown={(e) => e.stopPropagation()}
                                                    sx={{ height: 20, fontSize: "0.65rem", borderRadius: "6px" }}
                                                />
                                            ))}
                                        </Box>
                                    )}
                                    sx={{ borderRadius: "10px" }}
                                >
                                    {activeFacilities.map(f => f.name)
                                        .filter(name => name && !(form.facilityNames || []).includes(name))
                                        .map(name => (
                                            <MenuItem key={name} value={name}>{name}</MenuItem>
                                        ))
                                    }
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl required fullWidth size="small" error={!!fieldErrors.role} disabled={form.status === 'inactive'}
                                sx={{ "& .MuiFormLabel-asterisk": { color: "#d32f2f" } }}>
                                <InputLabel>Role</InputLabel>
                                <Select value={form.role} label="Role *"
                                    onChange={e => {
                                        setForm(f => ({ ...f, role: e.target.value }));
                                        if (fieldErrors.role) setFieldErrors(prev => { const n = { ...prev }; delete n.role; return n; });
                                    }}
                                    sx={{ borderRadius: "10px" }}
                                    disabled={form.status === 'inactive' || fetchingRoles}
                                >
                                    {fetchingRoles ? (
                                        <MenuItem disabled><CircularProgress size={20} sx={{ mr: 1 }} /> Loading roles...</MenuItem>
                                    ) : (
                                        roles.map((role) => {
                                            const roleKey = role.valueName.toUpperCase().replace(/\s+/g, '_');
                                            const mappedKey = roleKey === 'FACILITY_MANAGER' ? 'FACILITY_ADMIN' : roleKey;
                                            const cfg = ROLE_CONFIG[mappedKey] || ROLE_CONFIG[role.valueName.toUpperCase()] || { label: role.valueName };
                                            return (
                                                <MenuItem key={role.id || roleKey} value={mappedKey}>
                                                    <Typography variant="body2" fontWeight={500} sx={{ color: TEXT_MAIN }}>
                                                        {cfg.label}
                                                    </Typography>
                                                </MenuItem>
                                            );
                                        })
                                    )}
                                </Select>
                                {fieldErrors.role && (
                                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                                        {fieldErrors.role}
                                    </Typography>
                                )}
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Status</InputLabel>
                                <Select value={form.status} label="Status"
                                    onChange={e => setForm(f => ({ ...f, status: e.target.value as "active" | "inactive" }))}
                                    sx={{ borderRadius: "10px" }}
                                    disabled={fetchingStatus}
                                >
                                    {fetchingStatus ? (
                                        <MenuItem disabled><CircularProgress size={20} sx={{ mr: 1 }} /> Loading status...</MenuItem>
                                    ) : (
                                        statusList.map((status) => (
                                            <MenuItem key={status.id} value={status.valueName.toLowerCase()}>
                                                {status.valueName}
                                            </MenuItem>
                                        ))
                                    )}
                                </Select>
                            </FormControl>
                        </Grid>
                        {!editingUser && (
                            <Grid item xs={12}>
                                <TextField required fullWidth size="small" label="Password"
                                    disabled={form.status === 'inactive'}
                                    type={showPass ? "text" : "password"} value={form.password}
                                    autoComplete="new-password"
                                    onChange={e => {
                                        const newPassword = e.target.value;
                                        setForm(f => ({ ...f, password: newPassword }));
                                        if (fieldErrors.password) {
                                            const error = validatePassword(newPassword);
                                            if (error) {
                                                setFieldErrors(prev => ({ ...prev, password: error }));
                                            } else {
                                                setFieldErrors(prev => { const n = { ...prev }; delete n.password; return n; });
                                            }
                                        }
                                    }}
                                    error={!!fieldErrors.password}
                                    helperText={fieldErrors.password}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton size="small" onClick={() => setShowPass(p => !p)}>
                                                    {showPass ? <HideIcon fontSize="small" /> : <ViewIcon fontSize="small" />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" }, "& .MuiFormLabel-asterisk": { color: "#d32f2f" } }} />
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2.5, gap: 1 }}>
                    <Button onClick={() => setDialogOpen(false)} sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, color: TEXT_SUB }}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleSave} sx={{
                        borderRadius: "10px", textTransform: "none", fontWeight: "normal",
                        bgcolor: "#48CAE4", color: "#000000", "&:hover": { bgcolor: "#3ab3cc" }, px: 3,
                    }}>
                        {editingUser ? "Update User" : "Create User"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Delete Confirm Dialog ─── */}
            <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth
                PaperProps={{ sx: { borderRadius: "20px" } }}>
                <DialogTitle sx={{ pb: 1, pt: 3, px: 3 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Avatar sx={{ bgcolor: "#FEE2E2", color: "#DC2626", borderRadius: "10px" }}><DeleteIcon /></Avatar>
                        <Typography fontWeight={800} sx={{ color: TEXT_MAIN }}>Delete User</Typography>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ px: 3 }}>
                    <Typography variant="body2" sx={{ color: TEXT_SUB }}>
                        Are you sure you want to remove <b style={{ color: TEXT_MAIN }}>{deleteTarget?.name}</b>?

                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2.5, gap: 1 }}>
                    <Button disabled={isDeleting} onClick={() => setDeleteTarget(null)} sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, color: TEXT_SUB }}>Cancel</Button>
                    <Button disabled={isDeleting} variant="contained" onClick={handleDelete} sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, bgcolor: "#DC2626", "&:hover": { bgcolor: "#B91C1C" }, px: 3 }}>
                        {isDeleting ? <CircularProgress size={20} color="inherit" /> : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Reset Password Dialog (UI only) ─── */}
            <Dialog
                open={!!resetTarget}
                onClose={() => { if (!isResetting) { setResetTarget(null); setResetUsername(""); setNewPassword(""); setConfirmPassword(""); setResetError(""); setNewPwdError(""); setConfirmPwdError(""); } }}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: "12px", p: 2, maxWidth: "500px" } }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: 600, color: "#48CAE4" }}>
                        Reset Password
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ pb: 3, display: "flex", flexDirection: "column", gap: 2.5 }}>
                    {resetError && (
                        <Alert
                            severity={resetError === "You do not have access to change the password of a Super Admin" ? "info" : "error"}
                            sx={{ mb: 2, borderRadius: "8px" }}
                        >
                            {resetError}
                        </Alert>
                    )}

                    <TextField
                        fullWidth
                        placeholder="Username"
                        type="text"
                        value={resetUsername}
                        onChange={(e) => setResetUsername(e.target.value)}
                        autoComplete="off"
                        disabled
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                                backgroundColor: "white",
                            },
                            "& .MuiInputBase-input.Mui-disabled": {
                                WebkitTextFillColor: "#000000",
                                color: "#000000",
                            },
                        }}
                    />

                    <TextField
                        fullWidth
                        placeholder="New Password"
                        type={showNewPass ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => {
                            setNewPassword(e.target.value);
                            if (resetError) setResetError("");
                            if (newPwdError) setNewPwdError("");
                        }}
                        error={!!newPwdError}
                        helperText={newPwdError}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowNewPass(!showNewPass)}
                                        edge="end"
                                        sx={{ color: "#637381" }}
                                    >
                                        {showNewPass ? <HideIcon /> : <ViewIcon />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        autoComplete="new-password"
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                                backgroundColor: "white",
                            },
                        }}
                    />

                    <TextField
                        fullWidth
                        placeholder="Confirm Password"
                        type={showConfirmPass ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => {
                            setConfirmPassword(e.target.value);
                            if (resetError) setResetError("");
                            if (confirmPwdError) setConfirmPwdError("");
                        }}
                        error={!!confirmPwdError}
                        helperText={confirmPwdError}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowConfirmPass(!showConfirmPass)}
                                        edge="end"
                                        sx={{ color: "#637381" }}
                                    >
                                        {showConfirmPass ? <HideIcon /> : <ViewIcon />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        autoComplete="new-password"
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                                backgroundColor: "white",
                            },
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, justifyContent: "flex-end", gap: 2 }}>
                    <Button
                        disabled={isResetting}
                        onClick={() => { setResetTarget(null); setResetUsername(""); setNewPassword(""); setConfirmPassword(""); setResetError(""); setNewPwdError(""); setConfirmPwdError(""); }}
                        sx={{
                            textTransform: "none", color: "#48CAE4", border: "1px solid #48CAE4",
                            borderRadius: "8px", px: 3, fontWeight: "500",
                            "&:hover": { backgroundColor: "rgba(72,202,228,0.04)", borderColor: "#3ab3cc" },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={isResetting}
                        onClick={handleResetPassword}
                        sx={{
                            textTransform: "none", borderRadius: "8px", px: 4, fontWeight: "normal",
                            bgcolor: "#48CAE4",
                            color: "#000000",
                            boxShadow: "0 4px 15px rgba(72,202,228,0.4)",
                            transition: "all 0.3s", minWidth: "100px",
                            "&:hover": {
                                bgcolor: "#3ab3cc",
                                transform: "translateY(-2px)",
                                boxShadow: "0 6px 20px rgba(72,202,228,0.6)",
                            },
                        }}
                    >
                        {isResetting ? <CircularProgress size={24} color="inherit" /> : "Reset Password"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};



// ─── Notifications Table ──────────────────────────────────────────────────────
const NotificationPreferencesTable: React.FC<{
    list: any[],
    loading: boolean,
    onEdit: (item: any) => void,
    onDelete: (item: any) => void
}> = ({ list, loading, onEdit, onDelete }) => {
    return (
        <Box sx={{ mt: 4 }}>
            <SectionCard
                title="Notification History"
                subtitle="Review and manage all historical notification configurations."
                icon={<BellIcon fontSize="small" />}
                iconColor="#D97706"
                iconBg="#FEF3C7"
            >
                <Box sx={{ overflowX: "auto" }}>
                    <TableContainer sx={{ borderRadius: "14px", border: `1px solid ${BORDER}`, position: "relative", minHeight: loading ? 150 : "auto" }}>
                        {loading && (
                            <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "rgba(255,255,255,0.7)", zIndex: 1 }}>
                                <CircularProgress size={32} />
                            </Box>
                        )}
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                                    {["PROFILE NAME", "MEDICATION", "MINS", "MISSED SHIFT", "DAILY REPORT", "CREATED AT", "UPDATED AT", "ACTIONS"].map(h => (
                                        <TableCell key={h} sx={{
                                            fontWeight: 800,
                                            color: "#64748B",
                                            fontSize: "0.72rem",
                                            textTransform: "uppercase",
                                            letterSpacing: "0.06em",
                                            borderBottom: `2px solid ${BORDER}`,
                                            py: 2,
                                            px: 2
                                        }}>
                                            {h}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {list.length === 0 && !loading ? (
                                    <TableRow>
                                        <TableCell colSpan={8} sx={{ textAlign: "center", py: 5, color: TEXT_SUB }}>
                                            No notification preferences found.
                                        </TableCell>
                                    </TableRow>
                                ) : list.map((p, idx) => (
                                    <TableRow key={p.id || idx} sx={{
                                        bgcolor: idx % 2 === 0 ? "#FFFFFF" : "#FAFBFC",
                                        "&:hover": { bgcolor: "#F1F5F9" },
                                        transition: "background 0.15s",
                                        "&:last-child td": { borderBottom: "none" },
                                    }}>
                                        <TableCell sx={{ py: 1.5, px: 2 }}>
                                            <Typography variant="body2" sx={{ color: TEXT_MAIN, fontWeight: 800, fontSize: '0.75rem' }}>
                                                {p.preferenceProfile || "Default"}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ py: 1.5, px: 2 }}>
                                            <Chip
                                                label={p.medicationRemindersEnabled ? "Enabled" : "Disabled"}
                                                size="small"
                                                sx={{
                                                    bgcolor: p.medicationRemindersEnabled ? "#E0F2FE" : "#F1F5F9",
                                                    color: p.medicationRemindersEnabled ? "#0369A1" : "#64748B",
                                                    fontWeight: 700, fontSize: "0.65rem", borderRadius: "6px", height: 20
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ py: 1.5, px: 2 }}>
                                            <Typography variant="body2" sx={{ color: "#334155", fontWeight: 600 }}>{p.medicationRemindersMins}m</Typography>
                                        </TableCell>
                                        <TableCell sx={{ py: 1.5, px: 2 }}>
                                            <Typography variant="body2" sx={{ color: p.missedShiftAlertsEnabled ? "#10B981" : "#94A3B8", fontWeight: 600 }}>
                                                {p.missedShiftAlertsEnabled ? "Yes" : "No"}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ py: 1.5, px: 2 }}>
                                            <Typography variant="body2" sx={{ color: p.incidentReportAlertsEnabled ? "#7C3AED" : "#94A3B8", fontWeight: 600 }}>
                                                {p.incidentReportAlertsEnabled ? "Yes" : "No"}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ py: 1.5, px: 2 }}>
                                            <Typography variant="caption" sx={{ color: TEXT_SUB }}>
                                                {p.createdAt ? new Date(p.createdAt).toLocaleDateString() : "N/A"}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ py: 1.5, px: 2 }}>
                                            <Typography variant="caption" sx={{ color: TEXT_SUB }}>
                                                {p.updatedAt ? new Date(p.updatedAt).toLocaleDateString() : "N/A"}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ py: 1.5, px: 2 }}>
                                            <Box sx={{ display: "flex", gap: 1 }}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => onEdit(p)}
                                                    sx={{ color: "#0EA5E9", bgcolor: "#F0F9FF", "&:hover": { bgcolor: "#E0F2FE" }, borderRadius: "50%", width: 32, height: 32 }}
                                                >
                                                    <EditIcon fontSize="inherit" sx={{ fontSize: 16 }} />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => onDelete(p)}
                                                    sx={{ color: "#EF4444", bgcolor: "#FEE2E2", "&:hover": { bgcolor: "#FECACA" }, borderRadius: "50%", width: 32, height: 32 }}
                                                >
                                                    <DeleteIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </SectionCard>
        </Box>
    );
};

// ─── Security Tab ─────────────────────────────────────────────────────────────
const SecurityProfilesTable: React.FC = () => {
    const [profiles, setProfiles] = useState<SecuritySettings[]>([]);
    const [loading, setLoading] = useState(true);
    const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" as "success" | "error" });
    const [editTarget, setEditTarget] = useState<SecuritySettings | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<SecuritySettings | null>(null);
    const [isUpdating, setIsUpdating] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [page, setPage] = useState(1);
    const [totalElements, setTotalElements] = useState(0);
    const PAGE_SIZE = 10;

    const fetchProfiles = useCallback(async () => {
        setLoading(true);
        try {
            const data: any = await securityService.getAllSecuritySettings(page - 1, PAGE_SIZE);
            // Handle both PageResponse and direct array
            if (Array.isArray(data)) {
                setProfiles(data);
                setTotalElements(data.length);
            } else if (data && data.content && Array.isArray(data.content)) {
                setProfiles(data.content);
                setTotalElements(data.totalElements || 0);
            } else {
                setProfiles([]);
                setTotalElements(0);
            }
        } catch (error) {
            console.error("Error fetching security profiles:", error);
            setSnack({ open: true, msg: "Failed to load security profiles.", sev: "error" });
        } finally {
            setLoading(false);
        }
    }, [page]);

    useEffect(() => {
        fetchProfiles();
    }, [fetchProfiles]);

    const handleUpdate = async () => {
        if (!editTarget || !editTarget.id) return;
        setIsUpdating(true);
        try {
            await securityService.updateSecurityProfile(editTarget.id, {
                settingProfile: editTarget.settingProfile,
                require2fa: editTarget.require2fa,
                sessionTimeoutMins: editTarget.sessionTimeoutMins,
                passwordExpiryDays: editTarget.passwordExpiryDays,
                facilityId: editTarget.facilityId
            });
            setSnack({ open: true, msg: "Security profile updated successfully!", sev: "success" });
            setEditTarget(null);
            fetchProfiles();
        } catch (error) {
            console.error("Error updating profile:", error);
            setSnack({ open: true, msg: "Failed to update security profile.", sev: "error" });
        } finally {
            setIsUpdating(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget?.id) return;
        setIsDeleting(true);
        try {
            await securityService.deleteSecurityProfile(deleteTarget.id);
            setSnack({ open: true, msg: "Security profile deleted successfully!", sev: "success" });
            setDeleteTarget(null);
            fetchProfiles();
        } catch (error) {
            console.error("Error deleting profile:", error);
            setSnack({ open: true, msg: "Failed to delete profile. Please try again.", sev: "error" });
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <Box sx={{ mt: 3 }}>
            <Snackbar open={snack.open} autoHideDuration={3500} onClose={() => setSnack(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
                <Alert severity={snack.sev} sx={{ borderRadius: "12px", fontWeight: 600 }}>{snack.msg}</Alert>
            </Snackbar>

            <SectionCard
                title="Security Profiles"
                subtitle="Review and manage different security policy profiles."
                icon={<LockIcon fontSize="small" />}
                iconColor="#DC2626"
                iconBg="#FEE2E2"
            >
                <Box sx={{ overflowX: "auto" }}>
                    <TableContainer sx={{ borderRadius: "14px", border: `1px solid ${BORDER}`, position: "relative", minHeight: loading ? 150 : "auto" }}>
                        {loading && (
                            <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "rgba(255,255,255,0.7)", zIndex: 1 }}>
                                <CircularProgress size={32} />
                            </Box>
                        )}
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                                    {["PROFILE NAME", "2FA", "SESSION TIMEOUT", "PASSWORD EXPIRY", "CREATED AT", "UPDATED AT", "ACTIONS"].map(h => (
                                        <TableCell key={h} sx={{
                                            fontWeight: 800,
                                            color: "#64748B",
                                            fontSize: "0.72rem",
                                            textTransform: "uppercase",
                                            letterSpacing: "0.06em",
                                            borderBottom: `2px solid ${BORDER}`,
                                            py: 2,
                                            px: 2
                                        }}>
                                            {h}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {profiles.length === 0 && !loading ? (
                                    <TableRow>
                                        <TableCell colSpan={7} sx={{ textAlign: "center", py: 5, color: TEXT_SUB }}>
                                            No security profiles found.
                                        </TableCell>
                                    </TableRow>
                                ) : profiles.map((p, idx) => (
                                    <TableRow key={p.id || idx} sx={{
                                        bgcolor: p.require2fa ? "#FEF2F2" : (idx % 2 === 0 ? "#FFFFFF" : "#FAFBFC"),
                                        "&:hover": { bgcolor: p.require2fa ? "#FEE2E2" : "#F1F5F9" },
                                        transition: "background 0.15s",
                                        "&:last-child td": { borderBottom: "none" },
                                    }}>
                                        <TableCell sx={{ py: 1.5, px: 2 }}>
                                            <Typography variant="body2" fontWeight={800} sx={{ color: TEXT_MAIN }}>{p.settingProfile}</Typography>
                                        </TableCell>
                                        <TableCell sx={{ py: 1.5, px: 2 }}>
                                            <Chip
                                                label={p.require2fa ? "Required" : "Optional"}
                                                size="small"
                                                sx={{
                                                    bgcolor: p.require2fa ? "#FEE2E2" : "#F1F5F9",
                                                    color: p.require2fa ? "#EF4444" : "#64748B",
                                                    fontWeight: 700, fontSize: "0.65rem", borderRadius: "6px",
                                                    height: 20
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ py: 1.5, px: 2 }}>
                                            <Typography variant="body2" sx={{ color: "#334155", fontWeight: 600, fontSize: "0.82rem" }}>{p.sessionTimeoutMins} mins</Typography>
                                        </TableCell>
                                        <TableCell sx={{ py: 1.5, px: 2 }}>
                                            <Typography variant="body2" sx={{ color: "#334155", fontWeight: 600, fontSize: "0.82rem" }}>{p.passwordExpiryDays} days</Typography>
                                        </TableCell>
                                        <TableCell sx={{ py: 1.5, px: 2 }}>
                                            <Typography variant="caption" sx={{ color: TEXT_SUB }}>{p.createdAt ? new Date(p.createdAt).toLocaleDateString() : "N/A"}</Typography>
                                        </TableCell>
                                        <TableCell sx={{ py: 1.5, px: 2 }}>
                                            <Typography variant="caption" sx={{ color: TEXT_SUB }}>{p.updatedAt ? new Date(p.updatedAt).toLocaleDateString() : "N/A"}</Typography>
                                        </TableCell>
                                        <TableCell sx={{ py: 1.5, px: 2 }}>
                                            <Box sx={{ display: "flex", gap: 1 }}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setEditTarget(p)}
                                                    sx={{ bgcolor: "#E0F2FE", color: "#0EA5E9", borderRadius: "50%", width: 32, height: 32, "&:hover": { bgcolor: "#BAE6FD" } }}
                                                >
                                                    <EditIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setDeleteTarget(p)}
                                                    sx={{ bgcolor: "#FEE2E2", color: "#EF4444", borderRadius: "50%", width: 32, height: 32, "&:hover": { bgcolor: "#FECACA" } }}
                                                >
                                                    <DeleteIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* ── Pagination ─── */}
                    {totalElements > PAGE_SIZE && (
                        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2.5, px: 1 }}>
                            <Pagination
                                count={Math.ceil(totalElements / PAGE_SIZE)}
                                page={page}
                                onChange={(_, p) => setPage(p)}
                                size="small"
                                showFirstButton
                                showLastButton
                                sx={{
                                    "& .MuiPaginationItem-root": {
                                        borderRadius: "10px",
                                        fontWeight: 800,
                                        fontSize: "0.75rem",
                                        color: TEXT_SUB,
                                        border: `1px solid ${BORDER}`,
                                        "&:hover": { bgcolor: "#F1F5F9" }
                                    },
                                    "& .Mui-selected": {
                                        bgcolor: `${PRIMARY}15 !important`,
                                        color: `${PRIMARY} !important`,
                                        borderColor: `${PRIMARY}50 !important`,
                                        fontWeight: 900
                                    }
                                }}
                            />
                        </Box>
                    )}
                </Box>
            </SectionCard>

            {/* ── Edit Profile Dialog ─── */}
            <Dialog open={!!editTarget} onClose={() => setEditTarget(null)} maxWidth="xs" fullWidth
                PaperProps={{ sx: { borderRadius: "20px", overflow: "hidden" } }}>
                <DialogTitle sx={{
                    background: `linear-gradient(135deg,${PRIMARY} 0%,#0096C7 100%)`,
                    color: "#fff", display: "flex", alignItems: "center", gap: 1.5, py: 2.5, px: 3,
                }}>
                    <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)", borderRadius: "10px", width: 38, height: 38 }}>
                        <EditIcon />
                    </Avatar>
                    <Box>
                        <Typography fontWeight={800} fontSize="1rem">Edit Security Profile</Typography>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.8)" }}>
                            {editTarget?.settingProfile}
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ pt: 3, pb: 1 }}>
                    <Stack spacing={2.5}>
                        <TextField fullWidth size="small" label="Profile Name *" value={editTarget?.settingProfile || ""}
                            onChange={e => setEditTarget(p => p ? ({ ...p, settingProfile: e.target.value }) : null)}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />

                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 1.5, borderRadius: "12px", border: `1.5px solid ${BORDER}`, bgcolor: "#FAFBFC" }}>
                            <Box>
                                <Typography variant="body2" fontWeight={700} sx={{ color: TEXT_MAIN }}>Two-Factor Auth</Typography>
                                <Typography variant="caption" sx={{ color: TEXT_SUB }}>Require 2FA for this profile</Typography>
                            </Box>
                            <Switch checked={editTarget?.require2fa || false}
                                onChange={e => setEditTarget(p => p ? ({ ...p, require2fa: e.target.checked }) : null)} />
                        </Box>

                        <TextField fullWidth size="small" label="Session Timeout (mins) *" type="number" value={editTarget?.sessionTimeoutMins || ""}
                            onChange={e => setEditTarget(p => p ? ({ ...p, sessionTimeoutMins: Number(e.target.value) }) : null)}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />

                        <TextField fullWidth size="small" label="Password Expiry (days) *" type="number" value={editTarget?.passwordExpiryDays || ""}
                            onChange={e => setEditTarget(p => p ? ({ ...p, passwordExpiryDays: Number(e.target.value) }) : null)}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2.5, gap: 1 }}>
                    <Button onClick={() => setEditTarget(null)} sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, color: TEXT_SUB }}>Cancel</Button>
                    <Button variant="contained" disabled={isUpdating} onClick={handleUpdate} sx={{
                        borderRadius: "10px", textTransform: "none", fontWeight: 700,
                        bgcolor: PRIMARY, "&:hover": { bgcolor: "#005F8E" }, px: 3,
                    }}>
                        {isUpdating ? <CircularProgress size={20} color="inherit" /> : "Update Profile"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Delete Confirmation Dialog ─── */}
            <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth
                PaperProps={{ sx: { borderRadius: "20px" } }}>
                <DialogTitle sx={{ py: 2.5, px: 3, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Avatar sx={{ bgcolor: "#FEE2E2", color: "#EF4444", width: 40, height: 40 }}>
                        <DeleteIcon />
                    </Avatar>
                    <Box>
                        <Typography fontWeight={800} fontSize="1.1rem">Delete Profile?</Typography>
                        <Typography variant="caption" sx={{ color: TEXT_SUB }}></Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ color: TEXT_MAIN }}>
                        Are you sure you want to delete the security profile <b>"{deleteTarget?.settingProfile}"</b>?
                        This will remove all associated configurations.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2.5, gap: 1 }}>
                    <Button onClick={() => setDeleteTarget(null)} sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, color: TEXT_SUB }}>
                        Cancel
                    </Button>
                    <Button variant="contained" disabled={isDeleting} onClick={handleDelete} sx={{
                        borderRadius: "10px", textTransform: "none", fontWeight: 700,
                        bgcolor: "#EF4444", "&:hover": { bgcolor: "#DC2626" }, px: 3,
                    }}>
                        {isDeleting ? <CircularProgress size={20} color="inherit" /> : "Delete Profile"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

// ─── Main Page ──────────────────────────────────────────────────────────────────
export const SettingsConfigPage: React.FC = () => {
    const { user } = useAuth();
    const isFacilityAdmin = user?.role === 'FACILITY_ADMIN';
    const { currentTenantId, tenant } = useTenant();

    // Stabilized FACILITY_ID with a fallback only after initialization check
    const FACILITY_ID = currentTenantId || "34c13c76-204d-43d5-90e3-56a50cfc4e84";

    // Keep track of latest fetch to avoid race condition on refresh
    const lastFetchId = useRef(0);

    const [activeTab, setActiveTab] = useState("org");
    const [saved, setSaved] = useState(false);
    const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" as "success" | "error" });

    const [shifts, setShifts] = useState<any[]>([]);
    const [shiftToDelete, setShiftToDelete] = useState<any | null>(null);
    const [isDeletingShift, setIsDeletingShift] = useState(false);
    const [gracePeriod, setGracePeriod] = useState("15");
    const [earlyCheckIn, setEarlyCheckIn] = useState("15");
    const [autoCheckout, setAutoCheckout] = useState("16");
    const [gpsConfig, setGpsConfig] = useState({ requireGps: true, geofenceRadius: "200", allowOffline: false });
    const [checkInPolicy, setCheckInPolicy] = useState<any | null>(null);
    const [policyName, setPolicyName] = useState("Standard");
    const [isPolicyLoading, setIsPolicyLoading] = useState(false);
    const [notif, setNotif] = useState<any>(null);
    const [notifList, setNotifList] = useState<any[]>([]);
    const [notifLoading, setNotifLoading] = useState(false);
    const [deleteNotifTarget, setDeleteNotifTarget] = useState<any | null>(null);
    const [isDeletingNotif, setIsDeletingNotif] = useState(false);
    const [security, setSecurity] = useState<SecuritySettings | null>(null);
    const [securityLoading, setSecurityLoading] = useState(false);
    const [isSavingAttendance, setIsSavingAttendance] = useState(false);
    const [rulesLoading, setRulesLoading] = useState(false);

    // Sync Policy fields with selected shift if applicable
    useEffect(() => {
        if (!policyName || policyName === "Standard") return;

        const matchingShift = shifts.find(s => s.label === policyName);
        if (matchingShift) {
            console.log(`Syncing policy settings from shift: ${policyName}`);
            if (matchingShift.gracePeriodMins !== undefined) setGracePeriod(String(matchingShift.gracePeriodMins));
            if (matchingShift.earlyCheckIn !== undefined) setEarlyCheckIn(String(matchingShift.earlyCheckIn));
            if (matchingShift.autoCheckOut !== undefined) setAutoCheckout(String(matchingShift.autoCheckOut));

            // Do not overwrite gpsConfig with shift-specific settings. 
            // GPS and offline settings are policy-wide and managed by CheckInPolicies.
        }
    }, [policyName, shifts]);
    const [ruleDialogOpen, setRuleDialogOpen] = useState(false);
    const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
    const [ruleForm, setRuleForm] = useState({
        label: "",
        start: "09:00",
        end: "17:00",
        type: "custom" as "morning" | "afternoon" | "night" | "evening" | "custom"
    });
    const [ruleFormErr, setRuleFormErr] = useState("");
    const [isRuleSubmitting, setIsRuleSubmitting] = useState(false);

    // (Uility moved outside)


    const fetchAttendanceRules = useCallback(async () => {
        if (!FACILITY_ID) return;

        const fetchId = ++lastFetchId.current;
        setRulesLoading(true);
        console.log(`Starting fetch [${fetchId}] for FACILITY_ID: ${FACILITY_ID}`);

        try {
            const response: any = await attendanceService.getAttendanceRules(FACILITY_ID);

            // IF another fetch started after this one, ignore this result to prevent race conditions
            if (fetchId !== lastFetchId.current) {
                console.log(`Ignoring result of fetch [${fetchId}] as fetch [${lastFetchId.current}] is now active.`);
                return;
            }

            console.log(`Processing fetch [${fetchId}] response:`, response);
            const rules = parseApiResponse<AttendanceRule>(response);

            const serverShifts = rules.map(rule => {
                const name = rule.shiftName || "Unknown";
                const lower = name.toLowerCase();
                return {
                    id: rule.id,
                    label: name,
                    start: rule.startTime ? rule.startTime.slice(0, 5) : "00:00",
                    end: rule.endTime ? rule.endTime.slice(0, 5) : "00:00",
                    gracePeriodMins: rule.gracePeriodMins,
                    earlyCheckIn: rule.earlyCheckIn,
                    autoCheckOut: rule.autoCheckOut,
                    requireGps: rule.requireGps,
                    geofenceRadius: rule.geofenceRadius,
                    allowOffline: rule.allowOffline,
                    color: lower.includes("morning") ? "#D97706" : lower.includes("afternoon") ? "#16A34A" : lower.includes("night") ? "#4B5563" : lower.includes("evening") ? "#7C3AED" : "#48CAE4",
                    bg: lower.includes("morning") ? "#FFFBEB" : lower.includes("afternoon") ? "#F0FDF4" : lower.includes("night") ? "#F8FAFC" : lower.includes("evening") ? "#F5F3FF" : "#E8F8FB",
                    type: (lower.includes("morning") ? "morning" : lower.includes("afternoon") ? "afternoon" : lower.includes("night") ? "night" : lower.includes("evening") ? "evening" : "custom") as any
                };
            });

            const finalMerged: any[] = [...serverShifts];
            const orderMap = { morning: 1, afternoon: 2, evening: 3, night: 4, custom: 5 };
            finalMerged.sort((a, b) => (orderMap[a.type as keyof typeof orderMap] || 9) - (orderMap[b.type as keyof typeof orderMap] || 9));

            setShifts(finalMerged);

            // ONLY set shift-specific timing grace periods from rules.
            // Other policy fields (autoCheckout, GPS) should come from fetchCheckInPolicy.
            if (rules.length > 0) {
                const firstRule = rules[0];
                if (firstRule.gracePeriodMins !== undefined) setGracePeriod(String(firstRule.gracePeriodMins));
                if (firstRule.earlyCheckIn !== undefined) setEarlyCheckIn(String(firstRule.earlyCheckIn));

                // allowOffline is typically in rules, keep it there if missing from policies
                if (firstRule.allowOffline !== undefined) {
                    setGpsConfig(prev => ({ ...prev, allowOffline: !!firstRule.allowOffline }));
                }
            }
        } catch (err) {
            console.error(`Fetch [${fetchId}] failed:`, err);
        } finally {
            if (fetchId === lastFetchId.current) {
                setRulesLoading(false);
            }
        }
    }, [FACILITY_ID]);

    const fetchCheckInPolicy = useCallback(async () => {
        if (!FACILITY_ID) return;

        setIsPolicyLoading(true);
        try {
            const response: any = await attendanceService.getCheckInPolicies(FACILITY_ID);
            console.log("Check-In Policies fetched:", response);

            const policies = parseApiResponse<CheckInPolicy>(response);
            if (policies && policies.length > 0) {
                const policy = policies[0];
                setCheckInPolicy(policy);
                if (policy.policyName) {
                    setPolicyName(policy.policyName);
                }

                // Source of truth for Policy fields
                if (policy.autoCheckoutHours !== undefined) {
                    setAutoCheckout(String(policy.autoCheckoutHours));
                }

                setGpsConfig(prev => ({
                    ...prev,
                    requireGps: policy.geofenceRadiusMeters !== false && policy.geofenceRadiusMeters !== 0,
                    geofenceRadius: String(typeof policy.geofenceRadiusMeters === 'number' ? policy.geofenceRadiusMeters : prev.geofenceRadius)
                }));
            } else {
                setCheckInPolicy(null);
            }
        } catch (err) {
            console.error("Failed to fetch check-in policies", err);
        } finally {
            setIsPolicyLoading(false);
        }
    }, [FACILITY_ID]);

    const fetchSecurity = useCallback(async () => {
        setSecurityLoading(true);
        try {
            const data = await securityService.getSecuritySettings(FACILITY_ID);
            if (data) {
                setSecurity(data);
            }
        } catch (err) {
            console.error("Failed to load security settings", err);
            setSnack({ open: true, msg: "Failed to fetch security settings.", sev: "error" });
        } finally {
            setSecurityLoading(false);
        }
    }, [FACILITY_ID]);

    const fetchNotifPrefs = useCallback(async () => {
        setNotifLoading(true);
        try {
            const response = await notificationService.getNotificationPreferences(FACILITY_ID);
            if (response && response.status === "success" && response.data) {
                const content = response.data.content || [];
                const prefsArray = Array.isArray(content) ? content : [response.data];
                const prefs = prefsArray[0];

                setNotifList(prefsArray);
                if (prefs) {
                    setNotif(prefs);
                } else {
                    setNotif({
                        medicationRemindersEnabled: false,
                        medicationRemindersMins: 15,
                        missedShiftAlertsEnabled: false,
                        incidentReportAlertsEnabled: false,
                        facilityId: FACILITY_ID
                    });
                }
            }
        } catch (err) {
            console.error("Failed to load notification preferences", err);
            setSnack({ open: true, msg: "Failed to fetch notification preferences.", sev: "error" });
        } finally {
            setNotifLoading(false);
        }
    }, []);

    useEffect(() => {
        // Only fetch if a facility is determined
        if (!FACILITY_ID) return;

        if (activeTab === "security") {
            fetchSecurity();
        } else if (activeTab === "notifications") {
            fetchNotifPrefs();
        } else if (activeTab === "attendance") {
            fetchAttendanceRules();
            fetchCheckInPolicy();
        }
    }, [activeTab, fetchSecurity, fetchNotifPrefs, fetchAttendanceRules, fetchCheckInPolicy, FACILITY_ID]);

    const handleUpdateNotif = async (updated: any) => {
        const oldNotif = notif;
        const oldList = [...notifList];

        // Optimistic update for both states
        setNotif(updated);
        setNotifList(prev => prev.map(p => p.id === updated.id ? updated : p));

        try {
            await notificationService.updateNotificationPreferences(updated);
            setSnack({ open: true, msg: "Notification preferences updated!", sev: "success" });
            // Refresh to get latest data (sync IDs etc if it was a new POST)
            if (!updated.id) fetchNotifPrefs();
        } catch (err) {
            console.error("Failed to update notification preferences", err);
            setSnack({ open: true, msg: "Failed to update preferences.", sev: "error" });
            // Revert on failure
            setNotif(oldNotif);
            setNotifList(oldList);
        }
    };

    const handleDeleteNotif = async () => {
        if (!deleteNotifTarget?.id) return;
        setIsDeletingNotif(true);
        const oldList = [...notifList];

        // Optimistic delete
        setNotifList(prev => prev.filter(p => p.id !== deleteNotifTarget.id));

        try {
            await notificationService.deleteNotificationPreference(deleteNotifTarget.id);
            setSnack({ open: true, msg: "Notification preference deleted successfully!", sev: "success" });
            setDeleteNotifTarget(null);
        } catch (err) {
            console.error("Error deleting notification:", err);
            setSnack({ open: true, msg: "Failed to delete notification preference.", sev: "error" });
            // Revert
            setNotifList(oldList);
        } finally {
            setIsDeletingNotif(false);
        }
    };



    // (UUID utility moved outside)


    const syncLock = useRef<Record<string, boolean>>({});
    const syncDebounce = useRef<Record<string, any>>({});

    const syncShiftToBackend = async (shiftId: string, currentShifts: any[]) => {
        // Prevent multiple simultaneous updates for the same shift
        if (syncLock.current[shiftId]) return;

        const shift = currentShifts.find(s => s.id === shiftId);
        if (!shift) return;

        syncLock.current[shiftId] = true;
        try {
            const payload = {
                shiftName: shift.label,
                startTime: shift.start.slice(0, 5),
                endTime: shift.end.slice(0, 5),
                gracePeriodMins: parseInt(gracePeriod) || 15,
                facilityId: FACILITY_ID,
                isActive: true
            };

            if (isUUID(shift.id)) {
                // If it's a valid UUID, just update it
                await attendanceService.updateAttendanceRule(shift.id, payload);
                console.log(`Successfully updated shift ${shift.label} via PUT.`);
                setSnack({ open: true, msg: `Synced ${shift.label} update to backend.`, sev: 'success' });
            } else {
                // Not a UUID? It might be a default shift that hasn't been saved yet.
                // We should check if it exists on the server by name first.
                const response: any = await attendanceService.getAttendanceRules(FACILITY_ID);
                const rules = parseApiResponse<AttendanceRule>(response);

                const existingMatch = rules.find(r => r.shiftName.trim().toLowerCase() === shift.label.trim().toLowerCase());

                if (existingMatch) {
                    // It exists on the server! Update it using its real ID.
                    await attendanceService.updateAttendanceRule(existingMatch.id, payload);
                    // Update local state to have the real ID now
                    setShifts(prev => prev.map(s => s.id === shiftId ? { ...s, id: existingMatch.id } : s));
                    console.log(`Matched default shift ${shift.label} to server ID and updated via PUT.`);
                } else {
                    // Doesn't exist on server yet? Create it via POST
                    const createRes: any = await attendanceService.createAttendanceRule(payload);
                    const created = createRes?.data || createRes;
                    if (created?.id) {
                        setShifts(prev => prev.map(s => s.id === shiftId ? { ...s, id: created.id } : s));
                    }
                    console.log(`Created new shift ${shift.label} via POST.`);
                }
            }
        } catch (err) {
            console.error("Failed to sync shift update to backend:", err);
            setSnack({ open: true, msg: "Connection issue: Failed to sync shift update.", sev: "error" });
        } finally {
            syncLock.current[shiftId] = false;
        }
    };

    const handleUpdateShift = (id: string, field: "start" | "end", value: string) => {
        // Immediate local state update
        const next = shifts.map(s => s.id === id ? { ...s, [field]: value } : s);
        setShifts(next);

        // Faster 500ms debounce for backend sync
        if (syncDebounce.current[id]) clearTimeout(syncDebounce.current[id]);
        syncDebounce.current[id] = setTimeout(() => {
            syncShiftToBackend(id, next);
        }, 500);
    };

    const handleAddShift = () => {
        setRuleForm({
            label: "",
            start: "09:00",
            end: "17:00",
            type: "custom"
        });
        setEditingRuleId(null);
        setRuleFormErr("");
        setRuleDialogOpen(true);
    };

    const handleSaveRuleFromModal = async () => {
        if (!ruleForm.label.trim()) {
            setRuleFormErr("Shift name is required.");
            return;
        }

        const isDuplicate = shifts.some(s =>
            s.label.trim().toLowerCase() === ruleForm.label.trim().toLowerCase() &&
            s.id !== editingRuleId
        );

        if (isDuplicate) {
            setRuleFormErr("A shift with this name already exists.");
            return;
        }

        setIsRuleSubmitting(true);
        setRuleFormErr("");

        try {
            const name = ruleForm.label;
            const lower = name.toLowerCase();

            // Payload based on the provided CURL structure
            const payload = {
                shiftName: name,
                startTime: ruleForm.start.slice(0, 5),
                endTime: ruleForm.end.slice(0, 5),
                gracePeriodMins: parseInt(gracePeriod) || 15,
                facilityId: FACILITY_ID,
                isActive: true
            };

            if (editingRuleId && isUUID(editingRuleId)) {
                // Call the PUT API to update the existing rule as requested
                const response: any = await attendanceService.updateAttendanceRule(editingRuleId, payload);
                console.log("Update API Response:", response);

                const updated = response?.data || response;
                const updatedName = updated.shiftName || name;
                const updatedLower = updatedName.toLowerCase();

                const updatedShiftData = {
                    id: editingRuleId,
                    label: updatedName,
                    start: updated.startTime ? updated.startTime.slice(0, 5) : ruleForm.start.slice(0, 5),
                    end: updated.endTime ? updated.endTime.slice(0, 5) : ruleForm.end.slice(0, 5),
                    color: updatedLower.includes("morning") ? "#D97706" : updatedLower.includes("afternoon") ? "#16A34A" : updatedLower.includes("night") ? "#4B5563" : updatedLower.includes("evening") ? "#7C3AED" : "#0077B6",
                    bg: updatedLower.includes("morning") ? "#FFFBEB" : updatedLower.includes("afternoon") ? "#F0FDF4" : updatedLower.includes("night") ? "#F8FAFC" : updatedLower.includes("evening") ? "#F5F3FF" : "#E0F2FE",
                    type: (updatedLower.includes("morning") ? "morning" : updatedLower.includes("afternoon") ? "afternoon" : updatedLower.includes("night") ? "night" : updatedLower.includes("evening") ? "evening" : "custom") as any
                };
                setShifts(prev => prev.map(s => s.id === editingRuleId ? updatedShiftData : s));
                setSnack({ open: true, msg: "Shift updated successfully via API!", sev: "success" });
            } else {
                // Call the POST API as requested
                const response: any = await attendanceService.createAttendanceRule(payload);
                console.log("Create API Response:", response);

                const created = response?.data || response;
                const createdName = created.shiftName || name;
                const createdLower = createdName.toLowerCase();

                const newShift = {
                    id: created.id || Math.random().toString(36).substr(2, 9),
                    label: createdName,
                    start: created.startTime ? created.startTime.slice(0, 5) : ruleForm.start.slice(0, 5),
                    end: created.endTime ? created.endTime.slice(0, 5) : ruleForm.end.slice(0, 5),
                    color: createdLower.includes("morning") ? "#D97706" : createdLower.includes("afternoon") ? "#16A34A" : createdLower.includes("night") ? "#4B5563" : createdLower.includes("evening") ? "#7C3AED" : "#48CAE4",
                    bg: createdLower.includes("morning") ? "#FFFBEB" : createdLower.includes("afternoon") ? "#F0FDF4" : createdLower.includes("night") ? "#F8FAFC" : createdLower.includes("evening") ? "#F5F3FF" : "#E8F8FB",
                    type: (createdLower.includes("morning") ? "morning" : createdLower.includes("afternoon") ? "afternoon" : createdLower.includes("night") ? "night" : createdLower.includes("evening") ? "evening" : "custom") as any
                };

                setShifts(prev => {
                    const filtered = prev.filter(s => s.label.trim().toLowerCase() !== createdName.trim().toLowerCase());
                    return [...filtered, newShift];
                });
                setSnack({ open: true, msg: `Shift "${createdName}" saved to backend!`, sev: "success" });
            }

            setRuleDialogOpen(false);
        } catch (err: any) {
            console.error("Failed to create shift:", err);
            const msg = err.response?.data?.message || err.message || "Failed to create shift on server.";
            setRuleFormErr(msg);
        } finally {
            setIsRuleSubmitting(false);
        }
    };

    const handleDeleteShift = async (id: string) => {
        const shift = shifts.find(s => s.id === id);
        if (!shift) return;

        if (shifts.length <= 1) {
            setSnack({ open: true, msg: "At least one shift rule is required.", sev: "error" });
            return;
        }

        if (isUUID(id)) {
            setShiftToDelete(shift);
        } else {
            // Not saved to backend yet, just remove from local state
            setShifts(prev => prev.map(s => s.id === id ? { ...s, _leaving: true } : s));
            setTimeout(() => {
                setShifts(prev => prev.filter(s => s.id !== id));
            }, 300);
        }
    };

    const confirmDeleteShift = async () => {
        if (!shiftToDelete) return;

        setIsDeletingShift(true);
        try {
            // Call the hard delete API as requested in the CURL
            await attendanceService.deleteAttendanceRule(shiftToDelete.id);

            // Success: Remove from local state
            setShifts(prev => prev.filter(s => s.id !== shiftToDelete.id));
            setSnack({ open: true, msg: `Shift "${shiftToDelete.label}" deleted permanently!`, sev: "success" });
            setShiftToDelete(null);
        } catch (err: any) {
            console.error("Failed to delete shift:", err);
            const msg = err.response?.data?.message || err.message || "Failed to delete from server.";
            setSnack({ open: true, msg: `Error: ${msg}`, sev: "error" });
        } finally {
            setIsDeletingShift(false);
        }
    };

    const handleSaveAttendance = async () => {
        if (!policyName.trim()) {
            setSnack({ open: true, msg: "Policy name is required.", sev: "error" });
            return;
        }
        setIsSavingAttendance(true);
        try {
            // Construct the policy payload based on current UI state and provided CURL structure
            const policyPayload = {
                policyName: policyName || "Standard",
                allowOffsiteCheckin: !gpsConfig.requireGps,
                requirePhoto: !!checkInPolicy?.requirePhoto,
                geofenceRadiusMeters: gpsConfig.requireGps ? (parseInt(gpsConfig.geofenceRadius) || 200) : false,
                autoCheckoutHours: parseInt(autoCheckout) || 16,
                autoCheckInOut: !!checkInPolicy?.autoCheckInOut,
                facilityId: FACILITY_ID
            };

            if (checkInPolicy?.id && isUUID(checkInPolicy.id)) {
                console.log(`Updating existing policy [${checkInPolicy.id}]`);
                const response = await attendanceService.updateCheckInPolicy(checkInPolicy.id, policyPayload);
                const parsed = parseApiResponse<CheckInPolicy>(response);
                if (parsed.length > 0) setCheckInPolicy(parsed[0]);
            } else {
                console.log('Creating new policy via POST');
                const response = await attendanceService.createCheckInPolicy(policyPayload);
                const parsed = parseApiResponse<CheckInPolicy>(response);
                if (parsed.length > 0) setCheckInPolicy(parsed[0]);
            }

            setSaved(true);
            setSnack({ open: true, msg: "Attendance policies successfully", sev: "success" });
            setTimeout(() => setSaved(false), 3000);
        } catch (err: any) {
            console.error("Failed to save attendance policy:", err);
            setSnack({ open: true, msg: "Failed to save configuration to server.", sev: "error" });
        } finally {
            setIsSavingAttendance(false);
        }
    };

    const handleUpdateSessionTimeout = async (timeout: number) => {
        if (!security) return;
        const oldSecurity = security;
        const updated = { ...security, sessionTimeoutMins: timeout, facilityId: FACILITY_ID };
        setSecurity(updated);

        try {
            if (security.id && security.facilityId === FACILITY_ID) {
                await securityService.updateSecurityProfile(security.id, {
                    ...updated
                });
            } else {
                // If the profile is "Default" or from another facility, create a new one for THIS facility
                const created = await securityService.updateSecuritySettings({
                    ...updated,
                    settingProfile: `Profile for ${FACILITY_ID.slice(0, 8)}`
                });
                setSecurity(created);
            }
            setSnack({ open: true, msg: "Session timeout updated successfully!", sev: "success" });
        } catch (err: any) {
            console.error("Failed to update session timeout", err);
            setSecurity(oldSecurity);
            setSnack({ open: true, msg: "Failed to update session timeout.", sev: "error" });
        }
    };

    const handleToggle2FA = async (require2fa: boolean) => {
        if (!security) return;
        const oldSecurity = security;
        const updated = { ...security, require2fa, facilityId: FACILITY_ID };
        setSecurity(updated);

        try {
            if (security.id && security.facilityId === FACILITY_ID) {
                await securityService.updateSecurityProfile(security.id, {
                    ...updated
                });
            } else {
                // Create new specific profile for this facility
                const created = await securityService.updateSecuritySettings({
                    ...updated,
                    settingProfile: `Profile for ${FACILITY_ID.slice(0, 8)}`
                });
                setSecurity(created);
            }
            setSnack({ open: true, msg: `2FA ${require2fa ? 'enabled' : 'disabled'} successfully!`, sev: "success" });
        } catch (err: any) {
            console.error("Failed to update 2FA setting", err);
            setSecurity(oldSecurity);
            setSnack({ open: true, msg: "Failed to update 2FA setting.", sev: "error" });
        }
    };

    const handleSave = async () => {
        if (activeTab === "security") {
            if (!security) return;
            try {
                if (security.id && security.facilityId === FACILITY_ID) {
                    await securityService.updateSecurityProfile(security.id, security);
                } else {
                    const created = await securityService.updateSecuritySettings({
                        ...security,
                        facilityId: FACILITY_ID,
                        settingProfile: `Profile for ${FACILITY_ID.slice(0, 8)}`
                    });
                    setSecurity(created);
                }
                setSaved(true);
                setSnack({ open: true, msg: "Configuration saved successfully!", sev: "success" });
                setTimeout(() => setSaved(false), 3000);
            } catch (err: any) {
                console.error("Failed to save security settings", err);
                setSnack({ open: true, msg: "Failed to save configuration. Please try again.", sev: "error" });
            }
        } else {
            setSaved(true);
            setSnack({ open: true, msg: "Configuration saved successfully!", sev: "success" });
            setTimeout(() => setSaved(false), 3000);
        }
    };
    const visibleTabs = TABS.filter(t => !isFacilityAdmin || (t.key !== 'users' && t.key !== 'master_data'));
    const activeTabMeta = visibleTabs.find(t => t.key === activeTab) || visibleTabs[0];

    return (
        <Box sx={{ background: BG_PAGE, minHeight: "100vh", py: { xs: 2, md: 3 }, px: 0, pt: 3 }}>
            <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
                <Alert severity={snack.sev} sx={{ borderRadius: "12px", fontWeight: 600 }}>{snack.msg}</Alert>
            </Snackbar>

            {/* ── Hero Header ── */}
            {/* <Box sx={{
                background: "linear-gradient(135deg,#0077B6 0%,#0096C7 60%,#48CAE4 100%)",
                borderRadius: "24px", p: { xs: 3, md: "28px 36px" }, mb: 3, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2,
                boxShadow: "0 12px 40px rgba(0,119,182,0.30)", position: "relative", overflow: "hidden",
            }}>
                <Box sx={{ position: "absolute", right: -30, top: -30, width: 200, height: 200, borderRadius: "50%", background: "rgba(255,255,255,0.07)" }} />
                <Box sx={{ position: "absolute", right: 80, bottom: -50, width: 130, height: 130, borderRadius: "50%", background: "rgba(255,255,255,0.05)" }} />
                <Box sx={{ display: "flex", alignItems: "center", gap: 2.5, position: "relative" }}>
                    <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)", backdropFilter: "blur(8px)", width: 56, height: 56, borderRadius: "16px", border: "2px solid rgba(255,255,255,0.3)" }}>
                        <SettingsIcon sx={{ color: "#fff", fontSize: 28 }} />
                    </Avatar>
                    <Box>
                        <Typography variant="h5" fontWeight={800} sx={{ color: "#fff", lineHeight: 1.1, letterSpacing: "-0.3px" }}>Configuration & Settings</Typography>
                        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)", mt: 0.4 }}>
                            Configuring <span style={{ fontWeight: 500, fontSize: '13px' }}>{tenant?.name || "Global Settings"}</span>: Manage attendance rules, users & security
                        </Typography>
                    </Box>
                </Box>
                {activeTab !== "users" && (
                    <Button variant="contained" startIcon={saved ? <CheckIcon /> : <SaveIcon />} onClick={handleSave}
                        sx={{
                            bgcolor: saved ? "#16A34A" : "rgba(255,255,255,0.18)", backdropFilter: "blur(8px)", color: "#fff",
                            border: `1.5px solid ${saved ? "#16A34A" : "rgba(255,255,255,0.35)"}`, borderRadius: "12px",
                            textTransform: "none", fontWeight: 700, px: 3.5, py: 1.1, fontSize: "0.9rem", boxShadow: "none",
                            transition: "all 0.3s", "&:hover": { bgcolor: saved ? "#15803D" : "rgba(255,255,255,0.28)" }, position: "relative",
                        }}>
                        {saved ? "Saved!" : "Save Changes"}
                    </Button>
                )}
            </Box> */}

            <Grid container spacing={3} alignItems="flex-start">
                {/* ── Sidebar ── */}
                <Grid item xs={12} md={3}>
                    <Card elevation={0} sx={{ borderRadius: "20px", border: `1px solid ${BORDER}`, bgcolor: CARD_BG, overflow: "hidden", position: "sticky", top: 16 }}>
                        <Box sx={{ px: 2.5, pt: 1, pb: 1 }}>
                            <Typography variant="caption" fontWeight={700} sx={{ color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.62rem" }}>
                                Settings Sections
                            </Typography>
                        </Box>
                        <Stack spacing={0.5} sx={{ px: 1.5, pb: 2 }}>
                            {visibleTabs.map(tab => {
                                const isActive = activeTab === tab.key;
                                return (
                                    <Box key={tab.key} onClick={() => setActiveTab(tab.key)} sx={{
                                        display: "flex", alignItems: "center", gap: 1.5, p: "10px 14px", borderRadius: "14px", cursor: "pointer",
                                        bgcolor: isActive ? tab.color + "12" : "transparent",
                                        border: `1.5px solid ${isActive ? tab.color + "30" : "transparent"}`,
                                        transition: "all 0.18s", "&:hover": { bgcolor: tab.color + "0D", borderColor: tab.color + "25" },
                                    }}>
                                        <Avatar sx={{ bgcolor: isActive ? tab.bg : "#F1F5F9", color: isActive ? tab.color : "#94A3B8", width: 36, height: 36, borderRadius: "10px", transition: "all 0.18s" }}>
                                            {tab.icon}
                                        </Avatar>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="body2" fontWeight={isActive ? 800 : 600}
                                                sx={{ color: isActive ? tab.color : TEXT_MAIN, fontSize: "0.84rem", lineHeight: 1.2 }}>{tab.label}</Typography>
                                            <Typography variant="caption" sx={{ color: TEXT_SUB, fontSize: "0.68rem" }}>{tab.desc}</Typography>
                                        </Box>
                                        {isActive && <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: tab.color, flexShrink: 0 }} />}
                                    </Box>
                                );
                            })}
                        </Stack>
                        <Divider />
                    </Card>
                </Grid>

                {/* ── Content ── */}
                <Grid item xs={12} md={9}>
                    <Box sx={{ mb: 2.5, display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Avatar sx={{ bgcolor: activeTabMeta.bg, color: activeTabMeta.color, width: 36, height: 36, borderRadius: "10px" }}>{activeTabMeta.icon}</Avatar>
                        <Box>
                            <Typography variant="h5" fontWeight="700" sx={{ mb: 0.5, color: '#1A2B45' }}>{activeTabMeta.label}</Typography>
                            <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500, fontSize: "0.85rem" }}>{activeTabMeta.desc}</Typography>
                        </Box>
                    </Box>

                    {/* ── ORGANIZATION ── */}
                    {activeTab === "org" && <FacilitiesTab />}

                    {/* ── ATTENDANCE ── */}
                    {activeTab === "attendance" && (
                        <>
                            {rulesLoading ? (
                                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 10 }}>
                                    <CircularProgress />
                                </Box>
                            ) : (
                                <>
                                    <SectionCard
                                        title="Shift Definitions"
                                        subtitle="Define working hours for each shift"
                                        icon={<ShiftIcon fontSize="small" />}
                                        iconColor="#48CAE4"
                                        iconBg="#E8F8FB"
                                        action={
                                            <Button
                                                variant="contained"
                                                startIcon={<PersonAddIcon sx={{ color: "#000000" }} />}
                                                onClick={handleAddShift}
                                                sx={{
                                                    borderRadius: "10px",
                                                    textTransform: "none",
                                                    fontWeight: "normal",
                                                    bgcolor: "#48CAE4",
                                                    color: "#000000",
                                                    "&:hover": { bgcolor: "#3ab3cc" },
                                                    px: 2,
                                                    height: 36
                                                }}
                                            >
                                                Add Shift
                                            </Button>
                                        }
                                    >
                                        <Stack spacing={1.5}>
                                            {shifts.map(shift => (
                                                <ShiftRow
                                                    key={shift.id}
                                                    label={shift.label}
                                                    icon={
                                                        shift.type === "morning" ? <MorningIcon fontSize="small" /> :
                                                            shift.type === "afternoon" ? <AfternoonIcon fontSize="small" /> :
                                                                shift.type === "night" ? <NightIcon fontSize="small" /> :
                                                                    shift.type === "evening" ? <EveningIcon fontSize="small" /> :
                                                                        <ShiftIcon fontSize="small" />
                                                    }
                                                    color={shift.color}
                                                    bg={shift.bg}
                                                    start={shift.start}
                                                    end={shift.end}
                                                    onStart={v => handleUpdateShift(shift.id, "start", v)}
                                                    onEnd={v => handleUpdateShift(shift.id, "end", v)}
                                                    onDelete={() => handleDeleteShift(shift.id)}
                                                />
                                            ))}
                                        </Stack>
                                    </SectionCard>
                                    <SectionCard title="Check-In Policies" subtitle="Configure tolerance windows, GPS geofencing, and punch behaviors." icon={<TimerIcon fontSize="small" />} iconColor="#48CAE4" iconBg="#E8F8FB">
                                        <Grid container spacing={2.5}>
                                            <Grid item xs={12}>
                                                <Box sx={{ p: 2, borderRadius: "14px", border: `1.5px solid ${BORDER}`, bgcolor: "#FAFBFC" }}>
                                                    <Typography variant="body2" fontWeight={700} sx={{ color: TEXT_MAIN, mb: 0.5, fontSize: "0.82rem" }}>Policy Name</Typography>
                                                    <Select
                                                        fullWidth
                                                        size="small"
                                                        value={policyName}
                                                        onChange={e => setPolicyName(e.target.value as string)}
                                                        sx={{
                                                            borderRadius: "10px",
                                                            bgcolor: "#fff",
                                                            "& .MuiOutlinedInput-root": { borderRadius: "10px" }
                                                        }}
                                                    >
                                                        <MenuItem value="Standard">Standard</MenuItem>
                                                        {shifts.map(shift => (
                                                            <MenuItem key={shift.id} value={shift.label}>{shift.label}</MenuItem>
                                                        ))}
                                                        {policyName !== "Standard" && !shifts.some(s => s.label === policyName) && (
                                                            <MenuItem value={policyName}>{policyName}</MenuItem>
                                                        )}
                                                    </Select>
                                                </Box>
                                            </Grid>
                                            {[
                                                { label: "Late Grace Period", desc: "Minutes after shift start before 'Late'.", value: gracePeriod, set: setGracePeriod, unit: "min" },
                                                { label: "Early Check-In", desc: "Minutes before shift start allowed.", value: earlyCheckIn, set: setEarlyCheckIn, unit: "min" },
                                                { label: "Auto Check-Out", desc: "Close shifts after X hours.", value: autoCheckout, set: setAutoCheckout, unit: "hrs" },
                                            ].map(f => (
                                                <Grid item xs={12} sm={4} key={f.label}>
                                                    <Box sx={{ p: 2, borderRadius: "14px", border: `1.5px solid ${BORDER}`, bgcolor: "#FAFBFC" }}>
                                                        <Typography variant="body2" fontWeight={700} sx={{ color: TEXT_MAIN, mb: 0.5, fontSize: '0.82rem' }}>{f.label}</Typography>
                                                        <TextField fullWidth size="small" type="number" value={f.value} onChange={e => f.set(e.target.value)}
                                                            InputProps={{ endAdornment: <InputAdornment position="end" sx={{ "& .MuiTypography-root": { fontSize: '0.75rem', fontWeight: 700 } }}>{f.unit}</InputAdornment> }}
                                                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px", bgcolor: "#fff" } }} />
                                                    </Box>
                                                </Grid>
                                            ))}

                                            <Grid item xs={12}>
                                                <Stack spacing={1.5}>
                                                    <ToggleRow
                                                        icon={<LocationIcon fontSize="small" />}
                                                        iconColor="#48CAE4"
                                                        iconBg="#E8F8FB"
                                                        title="Require GPS Verification"
                                                        desc="Force caregivers to share location when punching in/out."
                                                        checked={gpsConfig.requireGps}
                                                        onChange={v => setGpsConfig(prev => ({ ...prev, requireGps: v }))}
                                                        extra={gpsConfig.requireGps ? (
                                                            <TextField
                                                                size="small"
                                                                type="number"
                                                                label="Radius"
                                                                value={gpsConfig.geofenceRadius}
                                                                onChange={e => setGpsConfig(prev => ({ ...prev, geofenceRadius: e.target.value }))}
                                                                InputProps={{ endAdornment: <InputAdornment position="end">m</InputAdornment> }}
                                                                sx={{ width: 110, "& .MuiInputBase-root": { borderRadius: "8px", height: 34, bgcolor: "#fff" } }}
                                                            />
                                                        ) : undefined}
                                                    />
                                                    <ToggleRow
                                                        icon={<RefreshIcon fontSize="small" />}
                                                        iconColor="#48CAE4"
                                                        iconBg="#E8F8FB"
                                                        title="Allow Offline Punching"
                                                        desc="Enable check-in without internet; syncs automatically when online."
                                                        checked={gpsConfig.allowOffline}
                                                        onChange={v => setGpsConfig(prev => ({ ...prev, allowOffline: v }))}
                                                    />
                                                    <ToggleRow
                                                        icon={<ViewIcon fontSize="small" />}
                                                        iconColor="#48CAE4"
                                                        iconBg="#E8F8FB"
                                                        title="Require Photo Verification"
                                                        desc="Caregivers must take a selfie when punching in/out."
                                                        checked={checkInPolicy?.requirePhoto || false}
                                                        onChange={v => setCheckInPolicy((prev: any) => ({ ...prev, requirePhoto: v }))}
                                                    />
                                                    <ToggleRow
                                                        icon={<ShiftIcon fontSize="small" />}
                                                        iconColor="#48CAE4"
                                                        iconBg="#E8F8FB"
                                                        title="Automated Check-In/Out"
                                                        desc="Automatically detect shift start/end based on GPS proximity."
                                                        checked={checkInPolicy?.autoCheckInOut || false}
                                                        onChange={v => setCheckInPolicy((prev: any) => ({ ...prev, autoCheckInOut: v }))}
                                                    />
                                                </Stack>
                                            </Grid>
                                        </Grid>
                                    </SectionCard>

                                    <Box sx={{ mt: 1, display: "flex", justifyContent: "flex-end" }}>
                                        <Button
                                            variant="contained"
                                            startIcon={saved ? <CheckIcon /> : isSavingAttendance ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                            onClick={handleSaveAttendance}
                                            disabled={isSavingAttendance}
                                            sx={{
                                                borderRadius: "12px", textTransform: "none", fontWeight: "normal", px: 4, py: 1.2,
                                                bgcolor: saved ? "#16A34A" : "#48CAE4", color: saved ? "#ffffff" : "#000000",
                                                "&:hover": { bgcolor: saved ? "#15803D" : "#3ab3cc" },
                                                boxShadow: saved ? "none" : "0 4px 12px rgba(72,202,228,0.2)"
                                            }}
                                        >
                                            {saved ? "Rules Saved!" : isSavingAttendance ? "Saving..." : "Save Changes"}
                                        </Button>
                                    </Box>
                                </>
                            )}
                        </>
                    )}

                    {/* ── Delete Shift Confirmation Dialog ─── */}
                    <Dialog open={!!shiftToDelete} onClose={() => setShiftToDelete(null)} maxWidth="xs" fullWidth
                        PaperProps={{ sx: { borderRadius: "20px" } }}>
                        <DialogTitle sx={{ py: 2.5, px: 3, display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Avatar sx={{ bgcolor: "#FEE2E2", color: "#EF4444", width: 40, height: 40 }}>
                                <DeleteIcon />
                            </Avatar>
                            <Box>
                                <Typography fontWeight={800} fontSize="1.1rem">Remove Shift?</Typography>
                                <Typography variant="caption" sx={{ color: TEXT_SUB }}>This will permanently remove this shift definition.</Typography>
                            </Box>
                        </DialogTitle>
                        <DialogContent>
                            <Typography variant="body2" sx={{ color: TEXT_MAIN }}>
                                Are you sure you want to delete the <b>"{shiftToDelete?.label}"</b> shift? This action cannot be undone.
                            </Typography>
                        </DialogContent>
                        <DialogActions sx={{ px: 3, py: 2.5, gap: 1 }}>
                            <Button onClick={() => setShiftToDelete(null)} sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, color: TEXT_SUB }}>
                                Cancel
                            </Button>
                            <Button variant="contained" disabled={isDeletingShift} onClick={confirmDeleteShift} sx={{
                                borderRadius: "10px", textTransform: "none", fontWeight: 700,
                                bgcolor: "#EF4444", "&:hover": { bgcolor: "#DC2626" }, px: 3,
                            }}>
                                {isDeletingShift ? <CircularProgress size={20} color="inherit" /> : "Remove Shift"}
                            </Button>
                        </DialogActions>
                    </Dialog>

                    {/* ── NOTIFICATIONS ── */}
                    {/* {activeTab === "notifications" && (
                        <>
                            <SectionCard
                                title="Notification Preferences"
                                subtitle="Control when and how system notifications are triggered"
                                icon={<BellIcon fontSize="small" />}
                                iconColor="#0077B6"
                                iconBg="#E0F2FE"
                            >
                                {notifLoading && !notif ? (
                                    <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}><CircularProgress size={32} /></Box>
                                ) : (
                                    <Stack spacing={1.5}>
                                        <ToggleRow
                                            icon={<MedIcon fontSize="small" />}
                                            iconColor="#0077B6"
                                            iconBg="#E0F2FE"
                                            title="Medication Reminders"
                                            desc="Notify caregivers before medication is due."
                                            checked={notif?.medicationRemindersEnabled || false}
                                            onChange={v => handleUpdateNotif({ ...notif, medicationRemindersEnabled: v })}
                                            disabled={isFacilityAdmin}
                                            extra={notif?.medicationRemindersEnabled ? (
                                                <TextField
                                                    size="small"
                                                    type="number"
                                                    disabled={isFacilityAdmin}
                                                    value={notif?.medicationRemindersMins || 15}
                                                    onChange={e => handleUpdateNotif({ ...notif, medicationRemindersMins: Number(e.target.value) })}
                                                    InputProps={{ endAdornment: <InputAdornment position="end">min</InputAdornment> }}
                                                    sx={{ width: 110, "& .MuiInputBase-root": { borderRadius: "8px", height: 34, bgcolor: "#fff" } }}
                                                />
                                            ) : undefined}
                                        />
                                        <ToggleRow
                                            icon={<WarnIcon fontSize="small" />}
                                            iconColor="#0077B6"
                                            iconBg="#E0F2FE"
                                            title="Missed Shift Alerts"
                                            desc="Notify admins when a caregiver exceeds the grace period."
                                            checked={notif?.missedShiftAlertsEnabled || false}
                                            onChange={v => handleUpdateNotif({ ...notif, missedShiftAlertsEnabled: v })}
                                            disabled={isFacilityAdmin}
                                        />
                                        <ToggleRow
                                            icon={<ReportIcon fontSize="small" />}
                                            iconColor="#0077B6"
                                            iconBg="#E0F2FE"
                                            title="Daily Incident Report"
                                            desc="Send a daily email digest of clinical notes and incidents."
                                            checked={notif?.incidentReportAlertsEnabled || false}
                                            onChange={v => handleUpdateNotif({ ...notif, incidentReportAlertsEnabled: v })}
                                            disabled={isFacilityAdmin}
                                        />
                                    </Stack>
                                )}
                            </SectionCard>

                            {/* Notification History Table Logic Kept, but UI Hidden as requested */}
                    {/* <NotificationPreferencesTable
                                list={notifList}
                                loading={notifLoading}
                                onEdit={(item) => {
                                    setNotif(item);
                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                }}
                                onDelete={(item) => setDeleteNotifTarget(item)}
                            /> */}

                    {/* ── Delete Notif Confirmation Dialog ─── */}
                    {/* <Dialog open={!!deleteNotifTarget} onClose={() => setDeleteNotifTarget(null)} maxWidth="xs" fullWidth
                                PaperProps={{ sx: { borderRadius: "20px" } }}>
                                <DialogTitle sx={{ py: 2.5, px: 3, display: "flex", alignItems: "center", gap: 1.5 }}>
                                    <Avatar sx={{ bgcolor: "#FEE2E2", color: "#EF4444", width: 40, height: 40 }}>
                                        <DeleteIcon />
                                    </Avatar>
                                    <Box>
                                        <Typography fontWeight={800} fontSize="1.1rem">Delete Notification?</Typography>
                                        <Typography variant="caption" sx={{ color: TEXT_SUB }}>This will soft delete this configuration.</Typography>
                                    </Box>
                                </DialogTitle>
                                <DialogContent>
                                    <Typography variant="body2" sx={{ color: TEXT_MAIN }}>
                                        Are you sure you want to delete the notification profile for <b>"{deleteNotifTarget?.preferenceProfile || 'this facility'}"</b>?
                                    </Typography>
                                </DialogContent>
                                <DialogActions sx={{ px: 3, py: 2.5, gap: 1 }}>
                                    <Button onClick={() => setDeleteNotifTarget(null)} sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, color: TEXT_SUB }}>
                                        Cancel
                                    </Button>
                                    <Button variant="contained" disabled={isDeletingNotif} onClick={handleDeleteNotif} sx={{
                                        borderRadius: "10px", textTransform: "none", fontWeight: 700,
                                        bgcolor: "#EF4444", "&:hover": { bgcolor: "#DC2626" }, px: 3,
                                    }}>
                                        {isDeletingNotif ? <CircularProgress size={20} color="inherit" /> : "Delete Notification"}
                                    </Button>
                                </DialogActions>
                            </Dialog>
                        </>
                    )} */}

                    {/* ── USERS ── */}
                    {activeTab === "users" && <UsersTab />}

                    {/* ── MASTER DATA ── */}
                    {activeTab === "master_data" && <MasterDataTab readOnly={isFacilityAdmin} />}

                    {/* ── STOCK AVAILABILITY ── */}
                    {activeTab === "stock" && (
                        <Box sx={{}}>
                            <StockAvailabilityPage />
                        </Box>
                    )}

                    {/* ── LOW STOCK NOTIFICATION ── */}
                    {activeTab === "low_stock" && (
                        <Box sx={{}}>
                            <LowStockNotificationTab />
                        </Box>
                    )}

                    {/* ── SECURITY ── */}
                    {/* {activeTab === "security" && (
                        <>
                            <SectionCard title="Authentication & Session Control" subtitle="Manage access security thresholds for all application users" icon={<SecurityIcon fontSize="small" />} iconColor="#0077B6" iconBg="#E0F2FE">
                                <Stack spacing={1.5}>
                                    <ToggleRow
                                        icon={<TwoFAIcon fontSize="small" />}
                                        iconColor="#0077B6"
                                        iconBg="#E0F2FE"
                                        title="Two-Factor Authentication (2FA)"
                                        desc="Require all users to verify their identity with a second factor on login."
                                        checked={security?.require2fa || false}
                                        onChange={handleToggle2FA}
                                        loading={securityLoading}
                                        disabled={isFacilityAdmin}
                                    />
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: "14px 18px", borderRadius: "14px", border: `1.5px solid ${BORDER}`, bgcolor: "#FAFBFC", flexWrap: "wrap", "&:hover": { borderColor: "#DC262630", bgcolor: "#FEF2F2" }, transition: "all 0.18s", opacity: isFacilityAdmin ? 0.7 : 1, pointerEvents: isFacilityAdmin ? "none" : "auto" }}>
                                        <Avatar sx={{ bgcolor: "#E0F2FE", color: "#0077B6", width: 36, height: 36, borderRadius: "10px", flexShrink: 0 }}><TimerIcon fontSize="small" /></Avatar>
                                        <Box sx={{ flex: 1, minWidth: 160 }}>
                                            <Typography variant="body2" fontWeight={700} sx={{ color: TEXT_MAIN, lineHeight: 1.3 }}>Session Timeout</Typography>
                                            <Typography variant="caption" sx={{ color: TEXT_SUB }}>Automatically log out users after a period of inactivity.</Typography>
                                        </Box>
                                        <FormControl size="small" sx={{ width: { xs: '100%', sm: 160 } }}>
                                            <Select
                                                value={String(security?.sessionTimeoutMins || "30")}
                                                onChange={e => handleUpdateSessionTimeout(Number(e.target.value))}
                                                sx={{ borderRadius: "10px", bgcolor: "#fff" }}
                                                disabled={isFacilityAdmin}
                                            >
                                                <MenuItem value="15">15 minutes</MenuItem>
                                                <MenuItem value="30">30 minutes</MenuItem>
                                                <MenuItem value="60">1 hour</MenuItem>
                                                <MenuItem value="240">4 hours</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Box>
                                    <Box sx={{ p: 2, borderRadius: "12px", bgcolor: "#FFF7ED", border: "1.5px solid #FED7AA", display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                                        <LockIcon sx={{ color: "#D97706", mt: 0.2, fontSize: 18, flexShrink: 0 }} />
                                        <Typography variant="caption" sx={{ color: "#92400E", lineHeight: 1.6 }}>
                                            <b>Security Tip:</b> Enabling 2FA significantly reduces the risk of unauthorized access. We recommend a session timeout of 30 minutes or less for clinical environments.
                                        </Typography>
                                    </Box>
                                </Stack>
                            </SectionCard>
                        </>
                    )} */}
                </Grid>
            </Grid>
            {/* Attendance Rule Modal */}
            <Dialog open={ruleDialogOpen} onClose={() => setRuleDialogOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "20px" } }}>
                <DialogTitle sx={{ bgcolor: "#48CAE4", color: "#000000", display: "flex", alignItems: "center", gap: 1.5, py: 2.5, px: 3 }}>
                    <Avatar sx={{ bgcolor: "rgba(0, 0, 0, 0.08)", color: "#000000", borderRadius: "10px", width: 38, height: 38 }}>
                        <ShiftIcon />
                    </Avatar>
                    <Box>
                        <Typography fontWeight={800} fontSize="1rem">{editingRuleId ? "Edit Attendance Shift" : "Add Attendance Shift"}</Typography>
                        <Typography variant="caption" sx={{ color: "rgba(0, 0, 0, 0.7)" }}>
                            {editingRuleId ? "Update shift timing and label" : "Define a new working shift"}
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ pt: 3, pb: 1 }}>
                    {ruleFormErr && <Alert severity="error" sx={{ mb: 2, borderRadius: "10px" }}>{ruleFormErr}</Alert>}
                    <Stack spacing={2.5}>
                        <TextField
                            required
                            fullWidth size="small" label="Shift Name" value={ruleForm.label}
                            onChange={e => setRuleForm(f => ({ ...f, label: e.target.value }))}
                            placeholder="e.g. Morning, Evening, Custom"
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" }, "& .MuiFormLabel-asterisk": { color: "#d32f2f" } }}
                        />
                        <Box sx={{ display: "flex", gap: 2 }}>
                            <TextField
                                fullWidth size="small" type="time" label="Start Time" value={ruleForm.start}
                                onChange={e => setRuleForm(f => ({ ...f, start: e.target.value }))}
                                InputLabelProps={{ shrink: true }}
                                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                            />
                            <TextField
                                fullWidth size="small" type="time" label="End Time" value={ruleForm.end}
                                onChange={e => setRuleForm(f => ({ ...f, end: e.target.value }))}
                                InputLabelProps={{ shrink: true }}
                                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                            />
                        </Box>
                        <FormControl fullWidth size="small">
                            <InputLabel>Shift Type</InputLabel>
                            <Select
                                value={ruleForm.type} label="Shift Type"
                                onChange={e => setRuleForm(f => ({ ...f, type: e.target.value as any }))}
                                sx={{ borderRadius: "10px" }}
                            >
                                <MenuItem value="morning">Morning</MenuItem>
                                <MenuItem value="afternoon">Afternoon</MenuItem>
                                <MenuItem value="night">Night</MenuItem>
                                <MenuItem value="evening">Evening</MenuItem>
                                <MenuItem value="custom">Custom / Other</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2.5, gap: 1 }}>
                    <Button onClick={() => setRuleDialogOpen(false)} disabled={isRuleSubmitting} sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, color: TEXT_SUB }}>Cancel</Button>
                    <Button variant="contained" onClick={handleSaveRuleFromModal} disabled={isRuleSubmitting} sx={{ borderRadius: "10px", textTransform: "none", fontWeight: "normal", bgcolor: "#48CAE4", color: "#000000", "&:hover": { bgcolor: "#3ab3cc" }, "&.Mui-disabled": { bgcolor: "rgba(0,0,0,0.12)", color: "rgba(0,0,0,0.26)" }, px: 3 }}>
                        {isRuleSubmitting ? <CircularProgress size={20} color="inherit" /> : (editingRuleId ? "Update Shift" : "Add Shift")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
